/**
 * Translation dictionary — English source-of-truth, with sr/bs/hr translations.
 *
 * Adding new strings:
 *  1. Add the key under `en` first.
 *  2. Add a translation for every other locale (sr, bs, hr). Falling back to en
 *     when a key is missing is fine in dev but breaks user trust in prod, so
 *     please don't ship missing keys.
 *
 * Plural / interpolation: use simple `{name}` placeholders. Values are filled at
 * render-time by the `t()` helper in i18n.tsx.
 */
import type { SupportedLocale } from '@waylo/shared';

export type StringKey = keyof typeof DICTIONARY.en;

export const DICTIONARY = {
  en: {
    // App-wide
    'app.tagline': 'Share the way.',
    'app.connecting': 'Connecting…',
    'app.errorPrefix': "Couldn't connect to Waylo:",
    'app.retry': 'Retry',
    'app.openInTelegram.title': 'Waylo runs inside Telegram',
    'app.openInTelegram.body':
      'Open @WayloRide_bot in Telegram and tap the menu button next to the message field to use the app.',
    'app.openInTelegram.cta': 'Open in Telegram',
    'common.back': 'Back',
    'common.continue': 'Continue',
    'common.save': 'Save',
    'common.saving': 'Saving…',
    'common.saved': 'Saved',
    'common.cancel': 'Cancel',
    'common.submit': 'Submit',
    'common.optional': 'optional',
    'common.loading': 'Loading…',
    'common.working': 'Working…',
    'common.refTo': 'Reference cost',
    'common.perSeat': 'per seat',
    'common.seat_one': 'seat',
    'common.seat_other': 'seats',
    'common.rideStarred': '★',
    'nav.search': 'Search',
    'nav.bookings': 'Bookings',
    'nav.rides': 'Rides',
    'nav.profile': 'Profile',

    // Onboarding
    'onboarding.welcome.title': 'Welcome to Waylo',
    'onboarding.welcome.tagline': 'Share the way. Split the cost.',
    'onboarding.welcome.b1.title': 'Find people going your way',
    'onboarding.welcome.b1.body': 'Search rides on 5 intercity routes in Serbia.',
    'onboarding.welcome.b2.title': 'Cost-share, not commercial',
    'onboarding.welcome.b2.body': 'Rides are priced around a reference cost so it stays fair.',
    'onboarding.welcome.b3.title': 'All inside Telegram',
    'onboarding.welcome.b3.body': 'No new app to install. Sign in with your Telegram identity.',
    'onboarding.language.title': 'Choose your language',
    'onboarding.language.hint': 'You can change this anytime in your profile.',
    'onboarding.profile.title': 'Set up your profile',
    'onboarding.profile.hint': 'We pre-filled what we got from Telegram.',
    'onboarding.profile.displayName': 'Display name',
    'onboarding.profile.bio': 'Short bio (optional)',
    'onboarding.profile.bioPh': 'e.g. I commute Belgrade–Novi Sad most weekends.',
    'onboarding.profile.driver': 'I want to drive sometimes',
    'onboarding.profile.driverHint': "You can switch this on/off anytime.",
    'onboarding.rules.title': 'Community rules',
    'onboarding.rules.hint': 'A short read. Strictly enforced.',
    'onboarding.rules.r1.t': 'Cost-share, not a taxi.',
    'onboarding.rules.r1.b': 'Waylo is for sharing the real cost of a private trip — not a commercial transport service.',
    'onboarding.rules.r2.t': 'Be fair on price.',
    'onboarding.rules.r2.b':
      'The reference cost is a community guideline. You decide. Above 1.5× we show an advisory and your ride still publishes — passengers vote with their bookings.',
    'onboarding.rules.r3.t': 'Be respectful.',
    'onboarding.rules.r3.b': 'Harassment, dangerous driving, no-shows lead to suspension.',
    'onboarding.rules.r4.t': 'No personal contact info in profile.',
    'onboarding.rules.r4.b': 'Use the in-app Telegram chat.',
    'onboarding.rules.r5.t': 'Report issues.',
    'onboarding.rules.r5.b': 'Use the in-app report tool — moderators read every report.',
    'onboarding.rules.accept': "I've read the rules and the {termsLink}, and I agree to follow them.",
    'onboarding.rules.terms': 'terms',
    'onboarding.cta.continue': 'Continue',
    'onboarding.cta.getStarted': 'Get started',
    'onboarding.cta.acceptToContinue': 'Accept the rules to continue',

    // Home
    'home.welcomeBack': 'Welcome back',
    'home.title': 'Where are you headed?',
    'home.subtitle': "Find a seat on someone's trip, or offer seats in yours.",
    'home.cta.publish.t': 'Publish a ride',
    'home.cta.publish.b': "Offer seats on a trip you're making",
    'home.cta.becomeDriver.t': 'Offering a ride?',
    'home.cta.becomeDriver.b': 'Turn on driver mode in your profile',
    'home.from': 'From',
    'home.to': 'To',
    'home.date': 'Date',
    'home.search': 'Search rides',
    'home.profile': 'Profile',
    'home.shortcuts': 'Shortcuts',
    'home.s.publish.t': 'Publish a ride',
    'home.s.publish.b': 'Earn back trip costs',
    'home.s.bookings.t': 'My bookings',
    'home.s.bookings.b': "Trips you've requested",
    'home.s.rides.t': 'My rides',
    'home.s.rides.b': "Trips you're driving",
    'home.s.premium.t': 'Waylo Premium',
    'home.s.premium.b': 'Unlimited rides',

    // Search results
    'search.title': 'Available rides',
    'search.empty.title': 'No rides for this date yet',
    'search.empty.body': 'Be the first to publish — or check back later.',
    'search.empty.cta': 'Publish a ride',
    'search.error': "Couldn't load rides.",

    // Ride detail
    'ride.cost': 'Cost',
    'ride.cost.hint':
      'Reference cost for this route: {ref} {currency}. We show this so passengers can verify the price is fair.',
    'ride.cost.fair': 'fair price',
    'ride.cost.warn': 'above ref',
    'ride.cost.high': 'high',
    'ride.preferences': 'Preferences',
    'ride.pickup': 'Pickup',
    'ride.cancel': 'Cancel ride',
    'ride.cancelConfirm': 'Cancel this ride? Passengers will be notified.',
    'ride.cantLoad': "Couldn't load this ride.",
    'common.offline': "You're offline — some actions may not work.",
    'profile.tier.title': 'Driver tier',
    'profile.tier.toGo': '{n} rides to {next}',
    'profile.tier.max': 'Top tier reached 🏆',
    'city.more': 'Keep typing to narrow…',
    'my.driver.pastRides': 'Past rides',
    'my.driver.resolvedRequests': 'Resolved requests',
    'my.archive.title': 'Archive',
    'my.archive.tabRides': 'Past rides',
    'my.archive.tabRequests': 'Resolved requests',
    'my.archive.emptyRides': 'No past rides yet',
    'my.archive.emptyRequests': 'No resolved requests yet',
    'leaderboard.title': 'Leaderboard',
    'leaderboard.subtitle': 'Top drivers by completed rides',
    'leaderboard.empty.title': 'No rankings yet',
    'leaderboard.empty.body': 'The board fills up as drivers complete rides — be among the first!',
    'leaderboard.rides': '{n} rides',
    'home.s.leaderboard.t': 'Leaderboard',
    'home.s.leaderboard.b': 'Top drivers',
    'ride.requestSeat': 'Request seat',
    'ride.start': 'Start ride',
    'ride.finish': 'Finish ride',
    'ride.finishConfirm': 'Finish the ride? Any remaining passengers will be marked as completed.',
    'booking.arrived': "I've arrived",
    'booking.noShow': "Mark passenger as no-show",
    'booking.noShowConfirm': "Mark {name} as a no-show? This lowers their reliability and can't be undone.",
    'ride.messageToDriver': 'Message to driver (optional)',
    'ride.messageToDriverPh': "Hi! I'd like to book a seat for…",
    'ride.pref.smoking.yes': 'Smoking ok',
    'ride.pref.smoking.no': 'No smoking',
    'ride.pref.music.yes': 'Music ok',
    'ride.pref.music.no': 'Quiet ride',
    'ride.pref.pets.yes': 'Pets ok',
    'ride.pref.pets.no': 'No pets',
    'ride.pref.luggage': 'Luggage',
    'ride.pref.womenOnly': 'Women only',
    'ride.verified': 'Verified',
    'ride.driverRides': '{n} rides',

    // Publish
    'publish.driverModeFirst.t': 'Switch on Driver mode first',
    'publish.driverModeFirst.b':
      'Drivers publish rides on Waylo. You can toggle this on in your profile and switch back anytime.',
    'publish.openProfile': 'Open profile',
    'publish.submitted.title': 'Ride submitted',
    'publish.submitted.live': 'Your ride is live. Passengers can now request seats.',
    'publish.submitted.review':
      "Submitted — your ride is under review. You'll be notified when it goes live (typically <24h).",
    'publish.seeMyRides': 'See my rides',
    'common.today': 'Today',
    'common.tomorrow': 'Tomorrow',
    'search.empty.changeSearch': 'Change search',
    'onboarding.welcome.b4.title': 'Two roles, one app',
    'onboarding.welcome.b4.body':
      'Passengers find seats; drivers offer them. Switch anytime in your profile.',
    'share.cta': 'Share my trip with family',
    'share.text': 'Follow my Waylo trip:',
    'shared.subtitle': '{name} is travelling with Waylo',
    'shared.departure': 'Departure',
    'shared.driver': 'Driver',
    'shared.status.scheduled': 'Scheduled',
    'shared.status.onway': 'On the way',
    'shared.status.completed': 'Completed',
    'shared.status.cancelled': 'Cancelled',
    'shared.footer': 'Shared privately via Waylo. No exact location is shown.',
    'shared.unavailable.t': 'Trip unavailable',
    'shared.unavailable.b': 'This link may have expired or been turned off.',
    'pickup.step.t': 'Pickup point (optional)',
    'pickup.label': 'Pickup spot',
    'pickup.labelPh': 'e.g. in front of the bus station',
    'pickup.tapHint': 'Tap the map to drop a pin.',
    'pickup.useLocation': 'Use my current location',
    'pickup.pinSet': 'Pin set ✓',
    'pickup.optionalHint': 'Optional — helps passengers find you.',
    'pickup.cardTitle': 'Pickup point',
    'pickup.directions': 'Open in maps →',
    'pickup.afterBooking': 'Exact spot shown after your booking is approved.',
    'confirm.title': 'Trip confirmation',
    'confirm.done': 'Confirmed — see you there!',
    'confirm.pending': "Please confirm you're still coming.",
    'confirm.cta': "I'm still coming",
    'confirm.driver.done': 'Departure confirmed',
    'confirm.driver.pending': "Confirm you're still driving.",
    'confirm.driver.cta': "I'm still driving",
    'confirm.driverConfirmed': 'Driver confirmed departure',
    'confirm.driverPending': 'Awaiting driver confirmation',
    'comfort.ac': 'Air conditioning',
    'comfort.charger': 'USB charging',
    'comfort.middleSeat': 'Middle seat',
    'booking.luggage': 'Luggage',
    'booking.luggage.warn':
      'This driver prefers luggage up to {max}. You can still request — just mention it in your message.',
    'reliability.reliable': 'Reliable',
    'reliability.building': 'Building reputation',
    'reliability.label': 'Reliability',
    'reliability.whatis.q': 'What is this?',
    'reliability.whatis.a':
      'Reliability reflects how often this driver confirms, departs on time, and completes trips. It updates automatically from real trips — no one sets it manually.',
    'reliability.yours': 'Your reliability: {n}',
    'search.empty.setAlert': 'Set a route alert',
    'alert.set.title': 'Alert me about this route',
    'alert.set.body': "We'll ping you in Telegram when someone posts a ride on this route.",
    'alert.set.cta': 'Set alert',
    'alert.set.toast': "Done — we'll ping you in Telegram.",
    'alert.more': 'More options',
    'alert.maxPrice': 'Max price (optional)',
    'alert.delete': 'Delete alert',
    'profile.alerts.title': 'Route alerts',
    'booking.seats': 'Seats',
    'booking.companions': 'Co-passengers',
    'booking.companions.hint': 'Optional — name who is travelling with you so the driver knows.',
    'booking.companions.ph': 'Passenger {n} name',
    'publish.step.route.t': 'Pick the route',
    'publish.stepCount': 'Step {n} of {total}',
    'publish.route.incomplete': 'Pick both cities from the list to continue.',
    'publish.route.mode.preset': 'Predefined',
    'publish.route.mode.custom': 'Custom',
    'publish.route.custom.hint':
      "Pick any two cities. We'll suggest a fair price and add the route so others can find it.",
    'publish.step.route.via.t': 'Stops along the way (optional)',
    'publish.step.route.via.hint':
      'Pick cities you pass through. Passengers can board or get off at any of them.',
    'publish.step.route.via.placeholder': 'Type a city name…',
    'publish.step.route.via.empty': 'No cities match.',
    'ride.via.short': 'via {stops}',
    'ride.via.long': 'via {stops}',
    'publish.step.when.t': 'When are you driving?',
    'publish.step.when.date': 'Date',
    'publish.step.when.time': 'Departure time',
    'publish.step.when.flex': 'Flexibility',
    'publish.step.when.flexExact': 'Sharp — no flex',
    'publish.step.when.flex10': '± 10 minutes',
    'publish.step.when.flex30': '± 30 minutes',
    'publish.step.when.flex60': '± 1 hour',
    'publish.step.seats.t': 'How many seats?',
    'publish.step.approval.t': 'Approval mode',
    'publish.step.approval.hint': 'Manual lets you review each request before approving.',
    'publish.step.approval.manual': 'Manual',
    'publish.step.approval.auto': 'Auto-approve',
    'publish.step.cost.t': 'Cost per seat',
    'publish.step.cost.label': 'Per seat ({currency})',
    'publish.step.cost.refTitle': 'Reference cost',
    'publish.step.cost.refBody':
      'Suggestion only: {ref} {currency}. You decide — charge less if you just want company, or match the reference to split fuel + tolls.',
    'publish.step.cost.warn':
      'Above the community guideline. Passengers may pass on it — that’s up to you.',
    'publish.step.cost.reject':
      'Well above the guideline (over 1.5× reference). Likely to deter passengers — your call.',
    'publish.step.prefs.t': 'Preferences',
    'publish.step.prefs.music': 'Music allowed',
    'publish.step.prefs.smoking': 'Smoking allowed',
    'publish.step.prefs.pets': 'Pets allowed',
    'publish.step.prefs.women': 'Women-only ride',
    'publish.step.prefs.luggage': 'Max luggage per passenger',
    'publish.step.prefs.luggage.small': 'Small (cabin bag)',
    'publish.step.prefs.luggage.medium': 'Medium (large backpack)',
    'publish.step.prefs.luggage.large': 'Large (suitcase)',
    'publish.step.notes.t': 'Pickup details (optional)',
    'publish.step.notes.label': 'Pickup notes',
    'publish.step.notes.ph': 'e.g. Pick up at the central bus station, north exit.',
    'publish.step.review.t': 'Review your ride',
    'publish.step.review.route': 'Route',
    'publish.step.review.departure': 'Departure',
    'publish.step.review.seats': 'Seats',
    'publish.step.review.cost': 'Cost',
    'publish.step.review.approval': 'Approval',
    'publish.cta.next': 'Next',
    'publish.cta.publishing': 'Publishing…',
    'publish.cta.publish': 'Publish ride',
    'publish.error.overpriced':
      'Cost {cost} {currency} exceeds 1.5× reference. Please adjust.',

    // My rides
    'my.driver.title': 'Driver',
    'my.driver.tabRides': 'My rides',
    'my.driver.tabRequests': 'Incoming requests',
    'my.driver.publish': 'Publish',
    'my.driver.noRides.t': 'No rides yet',
    'my.driver.noRides.b':
      'Publish your first ride to find passengers heading your way.',
    'my.driver.noRequests.t': 'No requests',
    'my.driver.noRequests.b': 'Booking requests from passengers will appear here.',

    // My bookings
    'my.bookings.title': 'My bookings',
    'my.bookings.empty.t': 'Nothing booked yet',
    'my.bookings.empty.b': 'Find a ride and request a seat — bookings show up here.',
    'my.bookings.id': 'Booking #{id}',

    // Booking detail
    'booking.title': 'Booking',
    'booking.passenger': 'Passenger',
    'booking.details': 'Booking details',
    'booking.openRide': 'Open ride details',
    'booking.chatWith': 'Open chat with {name} on Telegram',
    'booking.rate.title': 'Rate this trip',
    'booking.rate.commentPh': 'Optional comment (will be visible publicly)',
    'booking.rate.submit': 'Submit rating',
    'booking.rate.sending': 'Sending…',
    'booking.decision.decline': 'Decline',
    'booking.decision.approve': 'Approve',
    'booking.complete': 'Mark as completed',
    'booking.cancel': 'Cancel booking',
    'booking.reportLink': 'Report an issue with this booking',
    'booking.report.reason': 'Reason',
    'booking.report.note': 'What happened?',
    'booking.report.submit': 'Submit report',
    'booking.report.reason.no_show': 'No-show',
    'booking.report.reason.dangerous_driving': 'Dangerous driving',
    'booking.report.reason.overcharging': 'Overcharging',
    'booking.report.reason.commercial_activity': 'Operating commercially',
    'booking.report.reason.harassment': 'Harassment',
    'booking.report.reason.spam': 'Spam',
    'booking.report.reason.other': 'Other',
    'booking.cantLoad': "Couldn't load this booking.",

    // Profile
    'profile.section.profile': 'Profile',
    'profile.section.car': 'Car details',
    'profile.section.car.hint': 'Helps passengers find you at pickup.',
    'profile.section.account': 'Account',
    'profile.logout': 'Log out',
    'profile.displayName': 'Display name',
    'profile.bio': 'Bio',
    'profile.driverMode': 'Driver mode',
    'profile.car.make': 'Make',
    'profile.car.model': 'Model',
    'profile.car.color': 'Color',
    'profile.car.plate': 'Plate (optional)',
    'profile.language': 'Language',
    'profile.save': 'Save profile',
    'profile.ridesCompleted': '{n} rides',

    // Public profile
    'public.about': 'About',

    // Premium
    'premium.title': 'Waylo is free — forever',
    'premium.subtitle': 'Founding Supporters chip in to help fund development.',
    'premium.tag': 'Founding Supporter',
    'premium.active': 'Active',
    'premium.price.value': '50 ⭐',
    'premium.price.cadence': '/ month · ≈ €1',
    'premium.features.title': 'What supporters get',
    'premium.features.l1': 'Unlimited ride publishing (the free tier already covers 15 / month)',
    'premium.features.l2': 'A Founding Supporter badge on your profile',
    'premium.features.l3': 'Priority support from the team',
    'premium.features.l4': 'You directly help fund Waylo — built by Triardor',
    'premium.disclaimer':
      "Waylo works fully without ever paying. Supporting is optional and paid in Telegram Stars — Telegram's in-app currency — so we never see your card.",
    'premium.cta.buy': 'Support Waylo · 50 ⭐',
    'premium.cta.renew': 'Renew support · 50 ⭐',
    'common.madeByTriardor': 'Made by Triardor',

    // Badges (displayed labels)
    'badge.founding_driver': 'founding driver',
    'badge.community_verified': 'community verified',
    'badge.trusted_route_regular': 'trusted route regular',
    'badge.premium': 'premium',
    'badge.consistent': 'consistent',
    'badge.first_ride': 'first ride',
    'badge.early_supporter': 'early supporter',
    'tier.newcomer': 'Newcomer',
    'tier.regular': 'Regular driver',
    'tier.reliable': 'Reliable driver',
    'tier.trusted': 'Trusted driver',
    'tier.ambassador': 'Ambassador',
    'profile.memberSince': 'Member since {date}',

    // Status badges on cards
    'status.full': 'Full',
    'status.under_review': 'Under review',
    'status.cancelled': 'Cancelled',
    'status.completed': 'Completed',
    'status.pending': 'pending',
    'status.approved': 'approved',
    'status.declined': 'declined',
    // Full booking-status enum from packages/db (one per DB value)
    'bs.pending': 'Pending',
    'bs.approved': 'Approved',
    'bs.declined': 'Declined',
    'bs.cancelled_by_passenger': 'You cancelled',
    'bs.cancelled_by_driver': 'Driver cancelled',
    'bs.completed': 'Completed',
    'bs.rated': 'Rated',
    'bs.auto_closed': 'Closed',
    'bs.no_show': 'No-show',
    'bs.under_review': 'Under review',

    // Luggage labels (used wherever the value is rendered)
    'luggage.small': 'Small',
    'luggage.medium': 'Medium',
    'luggage.large': 'Large',

    // Phone + privacy fields
    'legal.privacy': 'Privacy Policy',
    'legal.terms': 'Terms of Service',
    'calendar.tab': 'Calendar',
    'calendar.empty': 'No rides on this day.',
    'my.driver.tabList': 'List',
    'vibes.title': 'Travel vibes',
    'vibes.hint':
      'Your defaults. We pre-fill these into every ride you publish, and show them on your public profile so passengers know what to expect.',
    'vibes.notSet': '—',
    'vibes.yes': 'Yes',
    'vibes.no': 'No',
    'vibes.music': 'Music in the car',
    'vibes.smoking': 'Smoking allowed',
    'vibes.pets': 'Pets allowed',
    'vibes.womenOnly': 'Women-only rides',
    'vibes.chatLevel': 'How talkative am I?',
    'vibes.chat.quiet': 'Quiet',
    'vibes.chat.sometimes': 'Sometimes',
    'vibes.chat.chatty': 'Chatty',
    'vibes.luggage': 'Max luggage',
    'phone.label': 'Phone number',
    'phone.placeholder': '+381 60 123 4567',
    'phone.hint':
      'International format with country code. Only shown to confirmed trip partners (and only if you turn on sharing below).',
    'phone.invalid': 'Please use international format, e.g. +381601234567',
    'privacy.title': 'Privacy',
    'privacy.shareTelegram': 'Share my Telegram @username after a booking is approved',
    'privacy.sharePhone': 'Share my phone number after a booking is approved',
    'privacy.showBio': 'Show my bio on my public profile',
    'privacy.contactReveal':
      'Phone and Telegram username are revealed only AFTER a booking is approved — never in search results.',

    // Counterparty contact section (booking detail)
    'contact.title': 'Contact details',
    'contact.beforeApproval':
      'Contact info will appear here once the booking is approved.',
    'contact.tgUsername': 'Telegram',
    'contact.phone': 'Phone',
    'contact.callButton': 'Call',
    'contact.tgButton': 'Open chat in Telegram',
    'contact.notShared': 'Not shared',

    // Block user
    'block.button': 'Block this user',
    'block.confirm': 'Block {name}? They won\'t be able to book your rides, and you won\'t see each other.',
    'block.title': 'Blocked users',
    'block.empty': 'You haven\'t blocked anyone.',
    'block.unblock': 'Unblock',

    // Onboarding phone step
    'onboarding.phone.title': 'Add your phone (optional)',
    'onboarding.phone.hint':
      'You can add this later. Only shared with trip partners after booking approval — never in search results.',
    'onboarding.phone.skip': 'Skip for now',

    // Server error codes — Worker returns these; client looks them up.
    'err.UNAUTHORIZED': 'Please sign in again to continue.',
    'err.FORBIDDEN': 'You don\'t have permission to do that.',
    'err.NOT_FOUND': 'Not found.',
    'err.VALIDATION_FAILED': 'Some fields are incomplete or invalid.',
    'err.INVALID_INIT_DATA': 'Telegram authentication failed. Try reopening the app.',
    'err.ANTI_ABUSE_REJECT':
      "You've already posted this route a few times today. Waylo is for sharing trips, not running a commercial line — please try again tomorrow.",
    'err.FREE_TIER_LIMIT':
      'Free drivers can publish up to 4 rides per month. Upgrade to Premium for unlimited publishing.',
    'err.RIDE_NOT_FOUND': 'This ride no longer exists.',
    'err.RIDE_NOT_BOOKABLE': 'This ride can no longer accept bookings.',
    'err.SUSPENDED':
      'Your account is paused for repeated last-minute cancellations. It lifts automatically soon.',
    'err.RIDE_NOT_STARTED': "The driver hasn't started this ride yet.",
    'err.INSUFFICIENT_SEATS': 'Not enough seats remaining.',
    'err.DUPLICATE_BOOKING': 'You already have a booking on this ride.',
    'err.CANNOT_BOOK_OWN_RIDE': 'You can\'t book your own ride.',
    'err.ILLEGAL_TRANSITION': 'That action isn\'t allowed from the current booking status.',
    'err.RATE_LIMITED': 'Too many requests — please slow down and try again in a moment.',
    'err.ROUTE_NOT_FOUND': 'Route not available.',
    'err.NOT_A_DRIVER': 'Turn on Driver mode in your profile before publishing rides.',
    'err.INVALID_DEPARTURE': 'Departure date is invalid.',
    'err.DEPARTURE_TOO_SOON': 'Departure must be at least 5 minutes in the future.',
    'err.CANNOT_BLOCK_SELF': 'You can\'t block yourself.',
    'err.ALREADY_RATED': 'You\'ve already rated this trip.',
    'err.BOOKING_NOT_COMPLETED': 'You can rate the trip after it\'s completed.',
    'err.BOOKING_NOT_FOUND': 'This booking no longer exists.',
    'err.USER_NOT_FOUND': 'User not found.',
    'err.PAYMENTS_DISABLED': 'Payments aren\'t configured in this environment.',
    'err.UNKNOWN': 'Something went wrong. Please try again.',
    'err.INTERNAL': 'Server error. Please try again in a moment.',
    'err.SAFETY_WINDOW_CLOSED': 'Safety alerts can only be raised within 24h of the ride.',
    'err.NOT_PARTICIPANT': 'You are not part of this booking.',
    'error.cantLoad.t': 'Couldn’t load',
    'error.cantLoad.b': 'Network or server hiccup. Try again.',
    'error.cantLoad.retry': 'Retry',
    'error.cantLoad.retrying': 'Retrying…',

    // Safety section
    'safety.title': 'Safety',
    'safety.cardTitle': 'Feel unsafe? Get help now',
    'safety.cardBody':
      'Tap below if anything makes you uncomfortable during this trip. You can call emergency services and/or alert Waylo + your emergency contact in one tap.',
    'safety.openSheet': '🚨 I need help',
    'safety.sheet.title': 'Get help now',
    'safety.sheet.callEmergency': 'Call emergency services (112)',
    'safety.sheet.callEmergencyHint': 'European emergency number — connects to police, ambulance, or fire as needed.',
    'safety.sheet.notifyWaylo': 'Notify Waylo + my emergency contact',
    'safety.sheet.notifyWayloHint':
      "Waylo's safety team is alerted with this ride's details. Your emergency contact's phone is shown to you so you can call them.",
    'safety.sheet.note': 'What\'s happening? (optional)',
    'safety.sheet.notePh': 'e.g. The driver is making me uncomfortable.',
    'safety.sheet.notifySent': 'Waylo and your emergency contact have been notified.',
    'safety.sheet.notifyFailed': 'Could not notify. Please call emergency services if urgent.',
    'safety.sheet.callContactBtn': 'Call {name}',
    'safety.cancel': 'Cancel',

    // Profile: emergency contact
    'profile.emergency.title': 'Emergency contact',
    'profile.emergency.hint':
      'Only contacted if you tap the safety button during a ride. Never shared with other users.',
    'profile.emergency.name': 'Contact name',
    'profile.emergency.phone': 'Contact phone',

    // Weekday short labels (Mon-first)
    'weekday.mon': 'Mon',
    'weekday.tue': 'Tue',
    'weekday.wed': 'Wed',
    'weekday.thu': 'Thu',
    'weekday.fri': 'Fri',
    'weekday.sat': 'Sat',
    'weekday.sun': 'Sun',

    // Recurring rides
    'publish.step.repeat.t': 'Repeat every week?',
    'publish.step.repeat.hint':
      'Turn on if you do this trip on a regular schedule. We will publish a fresh ride for each chosen weekday, up to 4 weeks ahead.',
    'publish.step.repeat.off': 'One-off ride',
    'publish.step.repeat.on': 'Recurring',
    'publish.step.repeat.allWeekdays': 'Mon–Fri',
    'publish.step.repeat.weekend': 'Sat & Sun',
    'publish.step.repeat.pickDays': 'Pick days',
    'publish.step.repeat.atLeastOne': 'Pick at least one day',
    'publish.submitted.recurring':
      "Your recurring ride is live. Future rides will publish automatically — manage from My templates.",
    'home.s.templates.t': 'My templates',
    'home.s.templates.b': 'Recurring rides',
    'my.templates.title': 'Recurring rides',
    'my.templates.empty.t': 'No recurring rides yet',
    'my.templates.empty.b':
      "Turn on 'Recurring' when publishing a ride to set up a weekly pattern.",
    'my.templates.upcoming': '{n} upcoming rides',
    'my.templates.everyWeek': 'every {days} at {time}',
    'my.templates.cancel': 'Stop this template',
    'my.templates.cancelConfirm':
      'Stop the recurring ride and cancel all upcoming materialized rides?',
    'my.templates.cancelled': 'Template stopped. {n} upcoming rides cancelled.',

    'err.TEMPLATE_NOT_FOUND': 'Template not found.',

    // Refer-a-friend
    'refer.title': 'Invite friends',
    'refer.hint':
      'Waylo grows with its community. Share your code — the more people on your routes, the more rides for everyone.',
    'refer.code': 'Your code',
    'refer.stats': '{count} invited · {rewarded} now riding',
    'refer.share': 'Share invite',
    'refer.copied': 'Code copied',
    'refer.showQr': 'Show QR code',
    'refer.hideQr': 'Hide QR code',
    'refer.qrCaption': 'Scan with your phone camera to join Waylo',
    'refer.shareText':
      "Try Waylo — a free carpool inside Telegram. No commission, just real travel cost shared.\n\nUse my code {code}: {url}",
    'products.title': 'Check out our other products',
    'products.cta': 'Check it out →',

    'tripStatus.title': 'Trip updates',
    'tripStatus.driverTitle': 'Send a quick update',
    'tripStatus.driverHint':
      'Let passengers know what is happening. Each tap sends a Telegram message to everyone with an approved booking.',
    'tripStatus.empty': 'No updates posted yet.',
    'tripStatus.leaving_soon': "I'm leaving soon",
    'tripStatus.at_pickup': "I'm at the pickup",
    'tripStatus.departed': "We've departed",
    'tripStatus.arrived': 'Arrived at destination',
    'tripStatus.sent': 'Update sent',
    'tripStatus.windowClosed': 'You can post updates from 2h before to 24h after departure.',
    'err.STATUS_WINDOW_CLOSED': 'You can post updates from 2h before to 24h after departure.',
    'editRide.title': 'Edit ride',
    'editRide.lockedHint':
      'Someone has already booked, so the time, price and route are locked. Cancel and repost to change those.',
    'editRide.cantEdit': 'This ride can no longer be edited.',
    'editRide.save': 'Save changes',
    'editRide.noChanges': 'No changes yet',
    'segment.title': 'Where do you get on and off?',
    'segment.hint': 'This ride passes through stops — pick your part of the trip.',
    'segment.board': 'Get on at',
    'segment.alight': 'Get off at',
    'segment.invalid': 'Drop-off must be after pick-up.',
    'segment.price': 'Your fare',
    'segment.allow': 'Allow partial trips',
    'segment.allow.hint':
      'Passengers can book just part of the route (e.g. only to a stop). The price is split by distance.',
    'segment.yourTrip': 'Your trip: {from} → {to}',
  },

  sr: {
    'app.tagline': 'Podelite put.',
    'app.connecting': 'Povezivanje…',
    'app.errorPrefix': 'Nije moguće povezati se sa Waylo:',
    'app.retry': 'Pokušaj ponovo',
    'app.openInTelegram.title': 'Waylo se koristi unutar Telegrama',
    'app.openInTelegram.body':
      'Otvorite @WayloRide_bot u Telegramu i pritisnite plavo dugme menija pored polja za poruku.',
    'app.openInTelegram.cta': 'Otvori u Telegramu',
    'common.back': 'Nazad',
    'common.continue': 'Nastavi',
    'common.save': 'Sačuvaj',
    'common.saving': 'Čuvanje…',
    'common.saved': 'Sačuvano',
    'common.cancel': 'Otkaži',
    'common.submit': 'Pošalji',
    'common.optional': 'opciono',
    'common.loading': 'Učitavanje…',
    'common.working': 'Radim…',
    'common.refTo': 'Referentna cena',
    'common.perSeat': 'po sedištu',
    'common.seat_one': 'sedište',
    'common.seat_other': 'sedišta',
    'common.rideStarred': '★',
    'nav.search': 'Pretraga',
    'nav.bookings': 'Rezervacije',
    'nav.rides': 'Vožnje',
    'nav.profile': 'Profil',

    'onboarding.welcome.title': 'Dobrodošli u Waylo',
    'onboarding.welcome.tagline': 'Podelite put. Podelite troškove.',
    'onboarding.welcome.b1.title': 'Pronađite ljude koji idu istim putem',
    'onboarding.welcome.b1.body': 'Pretraga vožnji na 5 međugradskih linija u Srbiji.',
    'onboarding.welcome.b2.title': 'Podela troškova, ne komercijala',
    'onboarding.welcome.b2.body': 'Cene se kreću oko referentne cene da bi bile fer.',
    'onboarding.welcome.b3.title': 'Sve unutar Telegrama',
    'onboarding.welcome.b3.body': 'Nema instalacije aplikacije. Prijava preko Telegrama.',
    'onboarding.language.title': 'Izaberite jezik',
    'onboarding.language.hint': 'Možete promeniti u profilu u svakom trenutku.',
    'onboarding.profile.title': 'Podesite profil',
    'onboarding.profile.hint': 'Popunili smo polja koja smo dobili sa Telegrama.',
    'onboarding.profile.displayName': 'Prikazano ime',
    'onboarding.profile.bio': 'Kratak opis (opciono)',
    'onboarding.profile.bioPh':
      'npr. Putujem na relaciji Beograd–Novi Sad većinu vikenda.',
    'onboarding.profile.driver': 'Želim ponekad da vozim',
    'onboarding.profile.driverHint': 'Možete uključiti/isključiti u svakom trenutku.',
    'onboarding.rules.title': 'Pravila zajednice',
    'onboarding.rules.hint': 'Kratko štivo. Strogo se primenjuje.',
    'onboarding.rules.r1.t': 'Podela troškova, ne taksi.',
    'onboarding.rules.r1.b':
      'Waylo služi za podelu stvarnih troškova privatnog putovanja — ne za komercijalni prevoz.',
    'onboarding.rules.r2.t': 'Cena mora biti fer.',
    'onboarding.rules.r2.b':
      'Referentna cena je preporuka zajednice. Vi odlučujete. Iznad 1.5× pokazujemo upozorenje, ali vožnja se i dalje objavljuje — putnici glasaju rezervacijama.',
    'onboarding.rules.r3.t': 'Budite ljubazni.',
    'onboarding.rules.r3.b':
      'Uznemiravanje, opasna vožnja, nedolazak — vode ka suspenziji.',
    'onboarding.rules.r4.t': 'Bez ličnih kontakt podataka u profilu.',
    'onboarding.rules.r4.b': 'Koristite Telegram chat unutar aplikacije.',
    'onboarding.rules.r5.t': 'Prijavite probleme.',
    'onboarding.rules.r5.b':
      'Koristite ugrađenu prijavu — moderatori čitaju svaku prijavu.',
    'onboarding.rules.accept':
      'Pročitao/la sam pravila i {termsLink}, i pristajem da ih poštujem.',
    'onboarding.rules.terms': 'uslove',
    'onboarding.cta.continue': 'Nastavi',
    'onboarding.cta.getStarted': 'Počnimo',
    'onboarding.cta.acceptToContinue': 'Prihvatite pravila za nastavak',

    'home.welcomeBack': 'Dobrodošli ponovo',
    'home.title': 'Kuda ideš?',
    'home.subtitle': 'Nađite mesto na nečijem putovanju ili ponudite svoja.',
    'home.cta.publish.t': 'Objavi vožnju',
    'home.cta.publish.b': 'Ponudite mesta na putovanju koje planirate',
    'home.cta.becomeDriver.t': 'Nudite prevoz?',
    'home.cta.becomeDriver.b': 'Uključite režim vozača u profilu',
    'home.from': 'Od',
    'home.to': 'Do',
    'home.date': 'Datum',
    'home.search': 'Pretraži vožnje',
    'home.profile': 'Profil',
    'home.shortcuts': 'Prečice',
    'home.s.publish.t': 'Objavi vožnju',
    'home.s.publish.b': 'Pokrij troškove puta',
    'home.s.bookings.t': 'Moje rezervacije',
    'home.s.bookings.b': 'Putovanja koja ste tražili',
    'home.s.rides.t': 'Moje vožnje',
    'home.s.rides.b': 'Putovanja koja vozite',
    'home.s.premium.t': 'Waylo Premium',
    'home.s.premium.b': 'Neograničene vožnje',

    'search.title': 'Dostupne vožnje',
    'search.empty.title': 'Još nema vožnji za ovaj datum',
    'search.empty.body': 'Budite prvi koji objavljuje — ili pokušajte kasnije.',
    'search.empty.cta': 'Objavi vožnju',
    'search.error': 'Nije moguće učitati vožnje.',

    'ride.cost': 'Cena',
    'ride.cost.hint':
      'Referentna cena za ovu liniju: {ref} {currency}. Prikazujemo je da bi putnici mogli da provere da li je cena fer.',
    'ride.cost.fair': 'fer cena',
    'ride.cost.warn': 'iznad referentne',
    'ride.cost.high': 'visoka',
    'ride.preferences': 'Preference',
    'ride.pickup': 'Pokupljanje',
    'ride.cancel': 'Otkaži vožnju',
    'ride.cancelConfirm': 'Otkazati ovu vožnju? Putnici će biti obavešteni.',
    'ride.cantLoad': 'Nije moguće učitati vožnju.',
    'common.offline': 'Niste povezani — neke radnje možda neće raditi.',
    'profile.tier.title': 'Nivo vozača',
    'profile.tier.toGo': 'Još {n} vožnji do: {next}',
    'profile.tier.max': 'Najviši nivo 🏆',
    'city.more': 'Nastavite da kucate da suzite…',
    'my.driver.pastRides': 'Prošle vožnje',
    'my.driver.resolvedRequests': 'Rešeni zahtevi',
    'my.archive.title': 'Arhiva',
    'my.archive.tabRides': 'Prošle vožnje',
    'my.archive.tabRequests': 'Rešeni zahtevi',
    'my.archive.emptyRides': 'Još nema prošlih vožnji',
    'my.archive.emptyRequests': 'Još nema rešenih zahteva',
    'leaderboard.title': 'Rang lista',
    'leaderboard.subtitle': 'Najbolji vozači po završenim vožnjama',
    'leaderboard.empty.title': 'Još nema rangiranja',
    'leaderboard.empty.body': 'Lista se popunjava kako vozači završavaju vožnje — budite među prvima!',
    'leaderboard.rides': '{n} vožnji',
    'home.s.leaderboard.t': 'Rang lista',
    'home.s.leaderboard.b': 'Najbolji vozači',
    'ride.requestSeat': 'Zatraži sedište',
    'ride.start': 'Započni vožnju',
    'ride.finish': 'Završi vožnju',
    'ride.finishConfirm': 'Završiti vožnju? Preostali putnici će biti označeni kao završeni.',
    'booking.arrived': 'Stigao/la sam',
    'booking.noShow': 'Označi da se putnik nije pojavio',
    'booking.noShowConfirm': 'Označiti {name} kao da se nije pojavio/la? Ovo snižava pouzdanost i ne može se poništiti.',
    'ride.messageToDriver': 'Poruka vozaču (opciono)',
    'ride.messageToDriverPh': 'Zdravo! Želim da rezervišem sedište za…',
    'ride.pref.smoking.yes': 'Pušenje dozvoljeno',
    'ride.pref.smoking.no': 'Bez pušenja',
    'ride.pref.music.yes': 'Muzika dozvoljena',
    'ride.pref.music.no': 'Tiha vožnja',
    'ride.pref.pets.yes': 'Ljubimci dozvoljeni',
    'ride.pref.pets.no': 'Bez ljubimaca',
    'ride.pref.luggage': 'Prtljag',
    'ride.pref.womenOnly': 'Samo za žene',
    'ride.verified': 'Verifikovan',
    'ride.driverRides': '{n} vožnji',

    'publish.driverModeFirst.t': 'Prvo uključite režim vozača',
    'publish.driverModeFirst.b':
      'Vozači objavljuju vožnje na Waylo. Možete uključiti u profilu i isključiti u svakom trenutku.',
    'publish.openProfile': 'Otvori profil',
    'publish.submitted.title': 'Vožnja je poslata',
    'publish.submitted.live':
      'Vaša vožnja je objavljena. Putnici sada mogu da zatraže sedišta.',
    'publish.submitted.review':
      'Poslato — vaša vožnja je na pregledu. Obavestićemo vas kada bude objavljena (obično <24h).',
    'publish.seeMyRides': 'Pogledaj moje vožnje',
    'common.today': 'Danas',
    'common.tomorrow': 'Sutra',
    'search.empty.changeSearch': 'Promeni pretragu',
    'onboarding.welcome.b4.title': 'Dve uloge, jedna aplikacija',
    'onboarding.welcome.b4.body':
      'Putnici traže mesta, vozači ih nude. Promenite ulogu bilo kada u profilu.',
    'share.cta': 'Podeli vožnju sa porodicom',
    'share.text': 'Prati moju Waylo vožnju:',
    'shared.subtitle': '{name} putuje sa Waylo',
    'shared.departure': 'Polazak',
    'shared.driver': 'Vozač',
    'shared.status.scheduled': 'Zakazano',
    'shared.status.onway': 'Na putu',
    'shared.status.completed': 'Završeno',
    'shared.status.cancelled': 'Otkazano',
    'shared.footer': 'Privatno deljeno preko Waylo. Tačna lokacija se ne prikazuje.',
    'shared.unavailable.t': 'Vožnja nedostupna',
    'shared.unavailable.b': 'Link je možda istekao ili je isključen.',
    'pickup.step.t': 'Mesto polaska (opciono)',
    'pickup.label': 'Mesto preuzimanja',
    'pickup.labelPh': 'npr. ispred autobuske stanice',
    'pickup.tapHint': 'Dodirnite mapu da postavite oznaku.',
    'pickup.useLocation': 'Koristi moju lokaciju',
    'pickup.pinSet': 'Oznaka postavljena ✓',
    'pickup.optionalHint': 'Opciono — pomaže putnicima da vas nađu.',
    'pickup.cardTitle': 'Mesto preuzimanja',
    'pickup.directions': 'Otvori u mapama →',
    'pickup.afterBooking': 'Tačno mesto se prikazuje nakon odobrenja rezervacije.',
    'confirm.title': 'Potvrda vožnje',
    'confirm.done': 'Potvrđeno — vidimo se!',
    'confirm.pending': 'Potvrdite da i dalje dolazite.',
    'confirm.cta': 'I dalje dolazim',
    'confirm.driver.done': 'Polazak potvrđen',
    'confirm.driver.pending': 'Potvrdite da i dalje vozite.',
    'confirm.driver.cta': 'I dalje vozim',
    'confirm.driverConfirmed': 'Vozač je potvrdio polazak',
    'confirm.driverPending': 'Čeka se potvrda vozača',
    'comfort.ac': 'Klima',
    'comfort.charger': 'USB punjač',
    'comfort.middleSeat': 'Srednje sedište',
    'booking.luggage': 'Prtljag',
    'booking.luggage.warn':
      'Ovaj vozač preferira prtljag do {max}. I dalje možete zatražiti — samo to pomenite u poruci.',
    'reliability.reliable': 'Pouzdan',
    'reliability.building': 'Gradi reputaciju',
    'reliability.label': 'Pouzdanost',
    'reliability.whatis.q': 'Šta je ovo?',
    'reliability.whatis.a':
      'Pouzdanost pokazuje koliko često ovaj vozač potvrđuje, kreće na vreme i završava vožnje. Ažurira se automatski na osnovu stvarnih vožnji — niko je ne podešava ručno.',
    'reliability.yours': 'Vaša pouzdanost: {n}',
    'search.empty.setAlert': 'Postavi obaveštenje za liniju',
    'alert.set.title': 'Obavesti me o ovoj liniji',
    'alert.set.body': 'Javićemo vam u Telegramu kada neko objavi vožnju na ovoj liniji.',
    'alert.set.cta': 'Postavi obaveštenje',
    'alert.set.toast': 'Gotovo — javićemo vam u Telegramu.',
    'alert.more': 'Više opcija',
    'alert.maxPrice': 'Maks. cena (opciono)',
    'alert.delete': 'Obriši obaveštenje',
    'profile.alerts.title': 'Obaveštenja za linije',
    'booking.seats': 'Mesta',
    'booking.companions': 'Saputnici',
    'booking.companions.hint': 'Opciono — navedite ko putuje sa vama da vozač zna.',
    'booking.companions.ph': 'Ime putnika {n}',
    'publish.step.route.t': 'Izaberite liniju',
    'publish.stepCount': 'Korak {n} od {total}',
    'publish.route.incomplete': 'Izaberite oba grada sa liste da nastavite.',
    'publish.route.mode.preset': 'Predefinisana',
    'publish.route.mode.custom': 'Prilagođena',
    'publish.route.custom.hint':
      'Izaberite bilo koja dva grada. Predložićemo fer cenu i dodati liniju da je drugi mogu naći.',
    'publish.step.route.via.t': 'Usputne stanice (opciono)',
    'publish.step.route.via.hint':
      'Izaberite gradove kroz koje prolazite. Putnici mogu da uđu ili izađu u bilo kom od njih.',
    'publish.step.route.via.placeholder': 'Ukucajte ime grada…',
    'publish.step.route.via.empty': 'Nema podudaranja.',
    'ride.via.short': 'preko {stops}',
    'ride.via.long': 'preko {stops}',
    'publish.step.when.t': 'Kada vozite?',
    'publish.step.when.date': 'Datum',
    'publish.step.when.time': 'Vreme polaska',
    'publish.step.when.flex': 'Fleksibilnost',
    'publish.step.when.flexExact': 'Tačno — bez fleksa',
    'publish.step.when.flex10': '± 10 minuta',
    'publish.step.when.flex30': '± 30 minuta',
    'publish.step.when.flex60': '± 1 sat',
    'publish.step.seats.t': 'Koliko sedišta?',
    'publish.step.approval.t': 'Način odobravanja',
    'publish.step.approval.hint':
      'Ručno odobravanje vam dozvoljava da pregledate svaki zahtev pre prihvatanja.',
    'publish.step.approval.manual': 'Ručno',
    'publish.step.approval.auto': 'Auto-odobravanje',
    'publish.step.cost.t': 'Cena po sedištu',
    'publish.step.cost.label': 'Po sedištu ({currency})',
    'publish.step.cost.refTitle': 'Referentna cena',
    'publish.step.cost.refBody':
      'Samo preporuka: {ref} {currency}. Vi odlučujete — naplatite manje ako želite samo društvo, ili pratite referencu da podelite gorivo + putarine.',
    'publish.step.cost.warn':
      'Iznad preporučene cene. Putnici mogu da preskoče — odluka je vaša.',
    'publish.step.cost.reject':
      'Znatno iznad preporuke (preko 1.5× referente). Verovatno će odbiti putnike — odluka je vaša.',
    'publish.step.prefs.t': 'Preference',
    'publish.step.prefs.music': 'Muzika dozvoljena',
    'publish.step.prefs.smoking': 'Pušenje dozvoljeno',
    'publish.step.prefs.pets': 'Ljubimci dozvoljeni',
    'publish.step.prefs.women': 'Vožnja samo za žene',
    'publish.step.prefs.luggage': 'Maksimalan prtljag po putniku',
    'publish.step.prefs.luggage.small': 'Mali (ručni prtljag)',
    'publish.step.prefs.luggage.medium': 'Srednji (veliki ranac)',
    'publish.step.prefs.luggage.large': 'Veliki (kofer)',
    'publish.step.notes.t': 'Detalji pokupljanja (opciono)',
    'publish.step.notes.label': 'Napomene za pokupljanje',
    'publish.step.notes.ph':
      'npr. Pokupljanje na centralnoj autobuskoj stanici, severni izlaz.',
    'publish.step.review.t': 'Pregled vožnje',
    'publish.step.review.route': 'Linija',
    'publish.step.review.departure': 'Polazak',
    'publish.step.review.seats': 'Sedišta',
    'publish.step.review.cost': 'Cena',
    'publish.step.review.approval': 'Odobravanje',
    'publish.cta.next': 'Dalje',
    'publish.cta.publishing': 'Objavljivanje…',
    'publish.cta.publish': 'Objavi vožnju',
    'publish.error.overpriced':
      'Cena {cost} {currency} prekoračuje 1.5× referentne. Molimo prilagodite.',

    'my.driver.title': 'Vozač',
    'my.driver.tabRides': 'Moje vožnje',
    'my.driver.tabRequests': 'Pristigli zahtevi',
    'my.driver.publish': 'Objavi',
    'my.driver.noRides.t': 'Još nema vožnji',
    'my.driver.noRides.b':
      'Objavite prvu vožnju da pronađete putnike koji idu istim putem.',
    'my.driver.noRequests.t': 'Nema zahteva',
    'my.driver.noRequests.b': 'Zahtevi za rezervaciju od putnika će se pojaviti ovde.',

    'my.bookings.title': 'Moje rezervacije',
    'my.bookings.empty.t': 'Još nema rezervacija',
    'my.bookings.empty.b':
      'Pronađite vožnju i zatražite sedište — rezervacije se prikazuju ovde.',
    'my.bookings.id': 'Rezervacija #{id}',

    'booking.title': 'Rezervacija',
    'booking.passenger': 'Putnik',
    'booking.details': 'Detalji rezervacije',
    'booking.openRide': 'Otvori detalje vožnje',
    'booking.chatWith': 'Otvori chat sa {name} na Telegramu',
    'booking.rate.title': 'Oceni putovanje',
    'booking.rate.commentPh': 'Opcioni komentar (biće javno vidljiv)',
    'booking.rate.submit': 'Pošalji ocenu',
    'booking.rate.sending': 'Slanje…',
    'booking.decision.decline': 'Odbij',
    'booking.decision.approve': 'Odobri',
    'booking.complete': 'Označi kao završeno',
    'booking.cancel': 'Otkaži rezervaciju',
    'booking.reportLink': 'Prijavi problem sa rezervacijom',
    'booking.report.reason': 'Razlog',
    'booking.report.note': 'Šta se desilo?',
    'booking.report.submit': 'Pošalji prijavu',
    'booking.report.reason.no_show': 'Nedolazak',
    'booking.report.reason.dangerous_driving': 'Opasna vožnja',
    'booking.report.reason.overcharging': 'Preplaćivanje',
    'booking.report.reason.commercial_activity': 'Komercijalna delatnost',
    'booking.report.reason.harassment': 'Uznemiravanje',
    'booking.report.reason.spam': 'Spam',
    'booking.report.reason.other': 'Drugo',
    'booking.cantLoad': 'Nije moguće učitati rezervaciju.',

    'profile.section.profile': 'Profil',
    'profile.section.car': 'Podaci o automobilu',
    'profile.section.car.hint': 'Pomaže putnicima da vas pronađu prilikom pokupljanja.',
    'profile.section.account': 'Nalog',
    'profile.logout': 'Odjava',
    'profile.displayName': 'Prikazano ime',
    'profile.bio': 'Opis',
    'profile.driverMode': 'Režim vozača',
    'profile.car.make': 'Marka',
    'profile.car.model': 'Model',
    'profile.car.color': 'Boja',
    'profile.car.plate': 'Registracija (opciono)',
    'profile.language': 'Jezik',
    'profile.save': 'Sačuvaj profil',
    'profile.ridesCompleted': '{n} vožnji',

    'public.about': 'O korisniku',

    'premium.title': 'Waylo je besplatan — zauvek',
    'premium.subtitle': 'Osnivački članovi pomažu da finansiramo razvoj.',
    'premium.tag': 'Osnivački član',
    'premium.active': 'Aktivno',
    'premium.price.value': '50 ⭐',
    'premium.price.cadence': '/ mesec · ≈ 120 RSD',
    'premium.features.title': 'Šta podržavaoci dobijaju',
    'premium.features.l1':
      'Neograničeno objavljivanje vožnji (besplatni nivo već pokriva 15 / mesec)',
    'premium.features.l2': 'Bedž osnivačkog člana na profilu',
    'premium.features.l3': 'Prioritetna podrška tima',
    'premium.features.l4': 'Direktno pomažete razvoj Waylo-a — projekat kompanije Triardor',
    'premium.disclaimer':
      'Waylo radi u potpunosti bez plaćanja. Podrška je opciona i plaća se u Telegram Stars — Telegramovoj internoj valuti — pa nikada ne vidimo vašu karticu.',
    'premium.cta.buy': 'Podrži Waylo · 50 ⭐',
    'premium.cta.renew': 'Obnovi podršku · 50 ⭐',
    'common.madeByTriardor': 'Razvija Triardor',

    'badge.founding_driver': 'osnivački vozač',
    'badge.community_verified': 'verifikovan u zajednici',
    'badge.trusted_route_regular': 'pouzdan na liniji',
    'badge.premium': 'premium',
    'badge.consistent': 'pouzdan',
    'badge.first_ride': 'prva vožnja',
    'badge.early_supporter': 'rani sledbenik',
    'tier.newcomer': 'Pridošlica',
    'tier.regular': 'Redovan vozač',
    'tier.reliable': 'Pouzdan vozač',
    'tier.trusted': 'Poverljiv vozač',
    'tier.ambassador': 'Ambasador',
    'profile.memberSince': 'Član od {date}',

    'status.full': 'Popunjeno',
    'status.under_review': 'Na pregledu',
    'status.cancelled': 'Otkazano',
    'status.completed': 'Završeno',
    'status.pending': 'na čekanju',
    'status.approved': 'odobreno',
    'status.declined': 'odbijeno',
    'bs.pending': 'Na čekanju',
    'bs.approved': 'Odobreno',
    'bs.declined': 'Odbijeno',
    'bs.cancelled_by_passenger': 'Vi ste otkazali',
    'bs.cancelled_by_driver': 'Vozač je otkazao',
    'bs.completed': 'Završeno',
    'bs.rated': 'Ocenjeno',
    'bs.auto_closed': 'Zatvoreno',
    'bs.no_show': 'Nedolazak',
    'bs.under_review': 'Na pregledu',

    'luggage.small': 'Mali',
    'luggage.medium': 'Srednji',
    'luggage.large': 'Veliki',

    'legal.privacy': 'Politika privatnosti',
    'legal.terms': 'Uslovi korišćenja',
    'calendar.tab': 'Kalendar',
    'calendar.empty': 'Nema vožnji ovog dana.',
    'my.driver.tabList': 'Lista',
    'vibes.title': 'Stil putovanja',
    'vibes.hint':
      'Vaše podrazumevane vrednosti. Predpopunjavamo ih u svaku vožnju koju objavite i prikazujemo na javnom profilu, da putnici znaju šta da očekuju.',
    'vibes.notSet': '—',
    'vibes.yes': 'Da',
    'vibes.no': 'Ne',
    'vibes.music': 'Muzika u kolima',
    'vibes.smoking': 'Pušenje dozvoljeno',
    'vibes.pets': 'Ljubimci dozvoljeni',
    'vibes.womenOnly': 'Vožnja samo za žene',
    'vibes.chatLevel': 'Koliko sam pričljiv/a?',
    'vibes.chat.quiet': 'Tih',
    'vibes.chat.sometimes': 'Ponekad',
    'vibes.chat.chatty': 'Pričljiv',
    'vibes.luggage': 'Maks. prtljag',
    'phone.label': 'Broj telefona',
    'phone.placeholder': '+381 60 123 4567',
    'phone.hint':
      'Međunarodni format sa pozivnim brojem. Prikazuje se samo potvrđenim saputnicima (i samo ako uključite deljenje ispod).',
    'phone.invalid': 'Koristite međunarodni format, npr. +381601234567',
    'privacy.title': 'Privatnost',
    'privacy.shareTelegram': 'Deli moj Telegram @username nakon odobrene rezervacije',
    'privacy.sharePhone': 'Deli moj broj telefona nakon odobrene rezervacije',
    'privacy.showBio': 'Prikaži moj opis na javnom profilu',
    'privacy.contactReveal':
      'Telefon i Telegram username se prikazuju TEK nakon odobrene rezervacije — nikada u rezultatima pretrage.',

    'contact.title': 'Kontakt podaci',
    'contact.beforeApproval':
      'Kontakt podaci će se pojaviti ovde nakon što rezervacija bude odobrena.',
    'contact.tgUsername': 'Telegram',
    'contact.phone': 'Telefon',
    'contact.callButton': 'Pozovi',
    'contact.tgButton': 'Otvori chat na Telegramu',
    'contact.notShared': 'Nije podeljen',

    'block.button': 'Blokiraj korisnika',
    'block.confirm': 'Blokirati {name}? Neće moći da rezerviše vaše vožnje, niti ćete se viđati u aplikaciji.',
    'block.title': 'Blokirani korisnici',
    'block.empty': 'Niste nikoga blokirali.',
    'block.unblock': 'Odblokiraj',

    'onboarding.phone.title': 'Dodajte broj telefona (opciono)',
    'onboarding.phone.hint':
      'Možete dodati kasnije. Deli se samo sa saputnicima nakon odobrene rezervacije — nikada u pretrazi.',
    'onboarding.phone.skip': 'Preskoči za sada',

    'err.UNAUTHORIZED': 'Molimo prijavite se ponovo.',
    'err.FORBIDDEN': 'Nemate dozvolu za ovu radnju.',
    'err.NOT_FOUND': 'Nije pronađeno.',
    'err.VALIDATION_FAILED': 'Neka polja su nepopunjena ili nevažeća.',
    'err.INVALID_INIT_DATA': 'Telegram autentifikacija nije uspela. Probajte da ponovo otvorite aplikaciju.',
    'err.ANTI_ABUSE_REJECT':
      'Već ste danas više puta objavili ovu rutu. Waylo je za deljenje putovanja, ne za komercijalne linije — pokušajte ponovo sutra.',
    'err.FREE_TIER_LIMIT':
      'Besplatni vozači mogu objaviti do 4 vožnje mesečno. Pređite na Premium za neograničeno objavljivanje.',
    'err.RIDE_NOT_FOUND': 'Vožnja više ne postoji.',
    'err.RIDE_NOT_BOOKABLE': 'Vožnja više ne prima rezervacije.',
    'err.SUSPENDED':
      'Nalog je privremeno pauziran zbog ponovljenih otkazivanja u poslednji čas. Pauza se automatski ukida uskoro.',
    'err.RIDE_NOT_STARTED': 'Vozač još nije započeo vožnju.',
    'err.INSUFFICIENT_SEATS': 'Nema dovoljno slobodnih sedišta.',
    'err.DUPLICATE_BOOKING': 'Već imate rezervaciju na ovoj vožnji.',
    'err.CANNOT_BOOK_OWN_RIDE': 'Ne možete rezervisati sopstvenu vožnju.',
    'err.ILLEGAL_TRANSITION': 'Ova radnja nije moguća u trenutnom statusu rezervacije.',
    'err.RATE_LIMITED': 'Previše zahteva — sačekajte trenutak i pokušajte ponovo.',
    'err.ROUTE_NOT_FOUND': 'Linija nije dostupna.',
    'err.NOT_A_DRIVER': 'Uključite režim vozača u profilu pre objavljivanja vožnji.',
    'err.INVALID_DEPARTURE': 'Datum polaska je nevažeći.',
    'err.DEPARTURE_TOO_SOON': 'Polazak mora biti najmanje 5 minuta u budućnosti.',
    'err.CANNOT_BLOCK_SELF': 'Ne možete blokirati sebe.',
    'err.ALREADY_RATED': 'Već ste ocenili ovu vožnju.',
    'err.BOOKING_NOT_COMPLETED': 'Možete oceniti vožnju nakon završetka.',
    'err.BOOKING_NOT_FOUND': 'Rezervacija više ne postoji.',
    'err.USER_NOT_FOUND': 'Korisnik nije pronađen.',
    'err.PAYMENTS_DISABLED': 'Plaćanja nisu konfigurisana u ovom okruženju.',
    'err.UNKNOWN': 'Nešto je pošlo po zlu. Pokušajte ponovo.',
    'err.INTERNAL': 'Greška na serveru. Pokušajte za trenutak.',
    'err.SAFETY_WINDOW_CLOSED': 'Bezbednosno upozorenje moguće je samo u roku od 24h od vožnje.',
    'err.NOT_PARTICIPANT': 'Niste deo ove rezervacije.',
    'error.cantLoad.t': 'Nije moguće učitati',
    'error.cantLoad.b': 'Problem sa mrežom ili serverom. Pokušajte ponovo.',
    'error.cantLoad.retry': 'Pokušaj ponovo',
    'error.cantLoad.retrying': 'Pokušavam…',

    'safety.title': 'Bezbednost',
    'safety.cardTitle': 'Osećate nesigurnost? Pozovite pomoć',
    'safety.cardBody':
      'Pritisnite dugme ispod ako vam je tokom vožnje neprijatno. Možete pozvati hitne službe i/ili obavestiti Waylo + svoj kontakt za hitne slučajeve jednim potezom.',
    'safety.openSheet': '🚨 Treba mi pomoć',
    'safety.sheet.title': 'Pozovite pomoć',
    'safety.sheet.callEmergency': 'Pozovite hitne službe (112)',
    'safety.sheet.callEmergencyHint':
      'Evropski broj za hitne slučajeve — povezuje sa policijom, hitnom pomoći ili vatrogascima.',
    'safety.sheet.notifyWaylo': 'Obavesti Waylo + moj hitni kontakt',
    'safety.sheet.notifyWayloHint':
      'Waylo tim za bezbednost se obaveštava sa detaljima vožnje. Vaš hitni kontakt će vam biti prikazan da ga sami pozovete.',
    'safety.sheet.note': 'Šta se dešava? (opciono)',
    'safety.sheet.notePh': 'npr. Vozač me čini neprijatnim.',
    'safety.sheet.notifySent': 'Waylo i vaš hitni kontakt su obavešteni.',
    'safety.sheet.notifyFailed': 'Slanje nije uspelo. Pozovite hitne službe ako je hitno.',
    'safety.sheet.callContactBtn': 'Pozovi {name}',
    'safety.cancel': 'Otkaži',

    'profile.emergency.title': 'Hitni kontakt',
    'profile.emergency.hint':
      'Kontaktira se samo ako pritisnete dugme za bezbednost tokom vožnje. Nikada se ne deli sa drugim korisnicima.',
    'profile.emergency.name': 'Ime kontakta',
    'profile.emergency.phone': 'Telefon kontakta',

    'weekday.mon': 'Pon',
    'weekday.tue': 'Uto',
    'weekday.wed': 'Sre',
    'weekday.thu': 'Čet',
    'weekday.fri': 'Pet',
    'weekday.sat': 'Sub',
    'weekday.sun': 'Ned',

    'publish.step.repeat.t': 'Ponavljanje svake nedelje?',
    'publish.step.repeat.hint':
      'Uključite ako redovno vozite ovu rutu. Objavićemo vožnju za svaki izabrani dan, do 4 nedelje unapred.',
    'publish.step.repeat.off': 'Jednokratna vožnja',
    'publish.step.repeat.on': 'Ponavljajuća',
    'publish.step.repeat.allWeekdays': 'Pon–Pet',
    'publish.step.repeat.weekend': 'Sub i Ned',
    'publish.step.repeat.pickDays': 'Izaberite dane',
    'publish.step.repeat.atLeastOne': 'Izaberite bar jedan dan',
    'publish.submitted.recurring':
      'Vaša ponavljajuća vožnja je aktivna. Buduće vožnje će se objavljivati automatski — upravljajte iz Moje šablone.',
    'home.s.templates.t': 'Moje šablone',
    'home.s.templates.b': 'Ponavljajuće vožnje',
    'my.templates.title': 'Ponavljajuće vožnje',
    'my.templates.empty.t': 'Još nema ponavljajućih vožnji',
    'my.templates.empty.b':
      'Uključite „Ponavljajuća" prilikom objavljivanja vožnje da postavite nedeljni raspored.',
    'my.templates.upcoming': '{n} predstojećih vožnji',
    'my.templates.everyWeek': 'svakog {days} u {time}',
    'my.templates.cancel': 'Zaustavi ovaj šablon',
    'my.templates.cancelConfirm':
      'Zaustaviti ponavljajuću vožnju i otkazati sve predstojeće vožnje?',
    'my.templates.cancelled': 'Šablon zaustavljen. {n} predstojećih vožnji otkazano.',

    'err.TEMPLATE_NOT_FOUND': 'Šablon nije pronađen.',

    'refer.title': 'Pozovi prijatelje',
    'refer.hint':
      'Waylo raste sa svojom zajednicom. Podelite svoj kod — što više ljudi na vašim linijama, to više vožnji za sve.',
    'refer.code': 'Vaš kod',
    'refer.stats': '{count} pozvano · {rewarded} sada vozi',
    'refer.share': 'Pošalji poziv',
    'refer.copied': 'Kod je kopiran',
    'refer.showQr': 'Prikaži QR kod',
    'refer.hideQr': 'Sakrij QR kod',
    'refer.qrCaption': 'Skenirajte kamerom telefona da se pridružite Waylo-u',
    'refer.shareText':
      'Probaj Waylo — besplatan carpool unutar Telegrama. Bez provizije, samo realno deljenje troškova putovanja.\n\nKoristi moj kod {code}: {url}',
    'products.title': 'Pogledajte naše druge proizvode',
    'products.cta': 'Pogledaj →',

    'tripStatus.title': 'Ažuriranja vožnje',
    'tripStatus.driverTitle': 'Pošalji brzo ažuriranje',
    'tripStatus.driverHint':
      'Obavestite putnike o stanju. Svaki dodir šalje Telegram poruku svima sa odobrenom rezervacijom.',
    'tripStatus.empty': 'Još nema objavljenih ažuriranja.',
    'tripStatus.leaving_soon': 'Uskoro krećem',
    'tripStatus.at_pickup': 'Stigao sam na mesto pokupljanja',
    'tripStatus.departed': 'Krenuli smo',
    'tripStatus.arrived': 'Stigli smo na odredište',
    'tripStatus.sent': 'Ažuriranje poslato',
    'tripStatus.windowClosed': 'Ažuriranja možete slati od 2h pre do 24h posle polaska.',
    'err.STATUS_WINDOW_CLOSED': 'Ažuriranja možete slati od 2h pre do 24h posle polaska.',
    'editRide.title': 'Izmeni vožnju',
    'editRide.lockedHint':
      'Neko je već rezervisao, pa su vreme, cena i ruta zaključani. Otkaži i objavi ponovo da bi ih promenio.',
    'editRide.cantEdit': 'Ova vožnja se više ne može izmeniti.',
    'editRide.save': 'Sačuvaj izmene',
    'editRide.noChanges': 'Još nema izmena',
    'segment.title': 'Gde ulaziš i izlaziš?',
    'segment.hint': 'Ova vožnja prolazi kroz usputne stanice — izaberi svoj deo puta.',
    'segment.board': 'Ulazak u',
    'segment.alight': 'Izlazak u',
    'segment.invalid': 'Izlazak mora biti posle ulaska.',
    'segment.price': 'Tvoja cena',
    'segment.allow': 'Dozvoli delimične vožnje',
    'segment.allow.hint':
      'Putnici mogu rezervisati samo deo rute (npr. do usputne stanice). Cena se deli po razdaljini.',
    'segment.yourTrip': 'Tvoj deo: {from} → {to}',
  },

  bs: {
    'app.tagline': 'Podijelite put.',
    'app.connecting': 'Povezivanje…',
    'app.errorPrefix': 'Nije moguće povezati se sa Waylo:',
    'app.retry': 'Pokušaj ponovo',
    'app.openInTelegram.title': 'Waylo se koristi unutar Telegrama',
    'app.openInTelegram.body':
      'Otvorite @WayloRide_bot u Telegramu i pritisnite plavo dugme menija pored polja za poruku.',
    'app.openInTelegram.cta': 'Otvori u Telegramu',
    'common.back': 'Nazad',
    'common.continue': 'Nastavi',
    'common.save': 'Sačuvaj',
    'common.saving': 'Čuvanje…',
    'common.saved': 'Sačuvano',
    'common.cancel': 'Otkaži',
    'common.submit': 'Pošalji',
    'common.optional': 'opciono',
    'common.loading': 'Učitavanje…',
    'common.working': 'Radim…',
    'common.refTo': 'Referentna cijena',
    'common.perSeat': 'po sjedištu',
    'common.seat_one': 'sjedište',

    'common.seat_other': 'sjedišta',
    'common.rideStarred': '★',
    'nav.search': 'Pretraga',
    'nav.bookings': 'Rezervacije',
    'nav.rides': 'Vožnje',
    'nav.profile': 'Profil',

    'onboarding.welcome.title': 'Dobrodošli u Waylo',
    'onboarding.welcome.tagline': 'Podijelite put. Podijelite troškove.',
    'onboarding.welcome.b1.title': 'Pronađite ljude koji idu istim putem',
    'onboarding.welcome.b1.body': 'Pretraga vožnji na 5 međugradskih linija.',
    'onboarding.welcome.b2.title': 'Podjela troškova, ne komercijala',
    'onboarding.welcome.b2.body': 'Cijene se kreću oko referentne cijene da budu fer.',
    'onboarding.welcome.b3.title': 'Sve unutar Telegrama',
    'onboarding.welcome.b3.body': 'Nema instalacije aplikacije. Prijava preko Telegrama.',
    'onboarding.language.title': 'Odaberite jezik',
    'onboarding.language.hint': 'Možete promijeniti u profilu bilo kada.',
    'onboarding.profile.title': 'Podesite profil',
    'onboarding.profile.hint': 'Popunili smo polja koja smo dobili sa Telegrama.',
    'onboarding.profile.displayName': 'Prikazano ime',
    'onboarding.profile.bio': 'Kratak opis (opciono)',
    'onboarding.profile.bioPh':
      'npr. Putujem na relaciji Beograd–Novi Sad većinu vikenda.',
    'onboarding.profile.driver': 'Želim ponekad da vozim',
    'onboarding.profile.driverHint': 'Možete uključiti/isključiti bilo kada.',
    'onboarding.rules.title': 'Pravila zajednice',
    'onboarding.rules.hint': 'Kratko štivo. Strogo se primjenjuje.',
    'onboarding.rules.r1.t': 'Podjela troškova, ne taksi.',
    'onboarding.rules.r1.b':
      'Waylo služi za podjelu stvarnih troškova privatnog putovanja — ne za komercijalni prevoz.',
    'onboarding.rules.r2.t': 'Cijena mora biti fer.',
    'onboarding.rules.r2.b':
      'Referentna cijena je preporuka zajednice. Vi odlučujete. Iznad 1.5× pokazujemo upozorenje, ali vožnja se i dalje objavljuje — putnici glasaju rezervacijama.',
    'onboarding.rules.r3.t': 'Budite ljubazni.',
    'onboarding.rules.r3.b':
      'Uznemiravanje, opasna vožnja, nedolazak — vode ka suspenziji.',
    'onboarding.rules.r4.t': 'Bez ličnih kontakt podataka u profilu.',
    'onboarding.rules.r4.b': 'Koristite Telegram chat unutar aplikacije.',
    'onboarding.rules.r5.t': 'Prijavite probleme.',
    'onboarding.rules.r5.b':
      'Koristite ugrađenu prijavu — moderatori čitaju svaku prijavu.',
    'onboarding.rules.accept':
      'Pročitao/la sam pravila i {termsLink}, i pristajem da ih poštujem.',
    'onboarding.rules.terms': 'uslove',
    'onboarding.cta.continue': 'Nastavi',
    'onboarding.cta.getStarted': 'Počnimo',
    'onboarding.cta.acceptToContinue': 'Prihvatite pravila za nastavak',

    'home.welcomeBack': 'Dobrodošli ponovo',
    'home.title': 'Kuda idete?',
    'home.subtitle': 'Nađite mjesto na nečijem putovanju ili ponudite svoja.',
    'home.cta.publish.t': 'Objavi vožnju',
    'home.cta.publish.b': 'Ponudite mjesta na putovanju koje planirate',
    'home.cta.becomeDriver.t': 'Nudite prijevoz?',
    'home.cta.becomeDriver.b': 'Uključite način vozača u profilu',
    'home.from': 'Od',
    'home.to': 'Do',
    'home.date': 'Datum',
    'home.search': 'Pretraži vožnje',
    'home.profile': 'Profil',
    'home.shortcuts': 'Prečice',
    'home.s.publish.t': 'Objavi vožnju',
    'home.s.publish.b': 'Pokri troškove puta',
    'home.s.bookings.t': 'Moje rezervacije',
    'home.s.bookings.b': 'Putovanja koja ste tražili',
    'home.s.rides.t': 'Moje vožnje',
    'home.s.rides.b': 'Putovanja koja vozite',
    'home.s.premium.t': 'Waylo Premium',
    'home.s.premium.b': 'Neograničene vožnje',

    'search.title': 'Dostupne vožnje',
    'search.empty.title': 'Još nema vožnji za ovaj datum',
    'search.empty.body': 'Budite prvi koji objavljuje — ili pokušajte kasnije.',
    'search.empty.cta': 'Objavi vožnju',
    'search.error': 'Nije moguće učitati vožnje.',

    'ride.cost': 'Cijena',
    'ride.cost.hint':
      'Referentna cijena za ovu liniju: {ref} {currency}. Prikazujemo je da putnici mogu provjeriti da li je cijena fer.',
    'ride.cost.fair': 'fer cijena',
    'ride.cost.warn': 'iznad referentne',
    'ride.cost.high': 'visoka',
    'ride.preferences': 'Preference',
    'ride.pickup': 'Pokupljanje',
    'ride.cancel': 'Otkaži vožnju',
    'ride.cancelConfirm': 'Otkazati ovu vožnju? Putnici će biti obaviješteni.',
    'ride.cantLoad': 'Nije moguće učitati vožnju.',
    'common.offline': 'Niste povezani — neke radnje možda neće raditi.',
    'profile.tier.title': 'Nivo vozača',
    'profile.tier.toGo': 'Još {n} vožnji do: {next}',
    'profile.tier.max': 'Najviši nivo 🏆',
    'city.more': 'Nastavite kucati da suzite…',
    'my.driver.pastRides': 'Prošle vožnje',
    'my.driver.resolvedRequests': 'Riješeni zahtjevi',
    'my.archive.title': 'Arhiva',
    'my.archive.tabRides': 'Prošle vožnje',
    'my.archive.tabRequests': 'Riješeni zahtjevi',
    'my.archive.emptyRides': 'Još nema prošlih vožnji',
    'my.archive.emptyRequests': 'Još nema riješenih zahtjeva',
    'leaderboard.title': 'Rang lista',
    'leaderboard.subtitle': 'Najbolji vozači po završenim vožnjama',
    'leaderboard.empty.title': 'Još nema rangiranja',
    'leaderboard.empty.body': 'Lista se popunjava kako vozači završavaju vožnje — budite među prvima!',
    'leaderboard.rides': '{n} vožnji',
    'home.s.leaderboard.t': 'Rang lista',
    'home.s.leaderboard.b': 'Najbolji vozači',
    'ride.requestSeat': 'Zatraži sjedište',
    'ride.start': 'Započni vožnju',
    'ride.finish': 'Završi vožnju',
    'ride.finishConfirm': 'Završiti vožnju? Preostali putnici će biti označeni kao završeni.',
    'booking.arrived': 'Stigao/la sam',
    'booking.noShow': 'Označi da se putnik nije pojavio',
    'booking.noShowConfirm': 'Označiti {name} kao da se nije pojavio/la? Ovo snižava pouzdanost i ne može se poništiti.',
    'ride.messageToDriver': 'Poruka vozaču (opciono)',
    'ride.messageToDriverPh': 'Zdravo! Želim rezervisati sjedište za…',
    'ride.pref.smoking.yes': 'Pušenje dozvoljeno',
    'ride.pref.smoking.no': 'Bez pušenja',
    'ride.pref.music.yes': 'Muzika dozvoljena',
    'ride.pref.music.no': 'Tiha vožnja',
    'ride.pref.pets.yes': 'Kućni ljubimci dozvoljeni',
    'ride.pref.pets.no': 'Bez kućnih ljubimaca',
    'ride.pref.luggage': 'Prtljag',
    'ride.pref.womenOnly': 'Samo za žene',
    'ride.verified': 'Verifikovan',
    'ride.driverRides': '{n} vožnji',

    'publish.driverModeFirst.t': 'Prvo uključite vozački režim',
    'publish.driverModeFirst.b':
      'Vozači objavljuju vožnje na Waylo. Možete uključiti u profilu i isključiti bilo kada.',
    'publish.openProfile': 'Otvori profil',
    'publish.submitted.title': 'Vožnja je poslata',
    'publish.submitted.live':
      'Vaša vožnja je objavljena. Putnici sada mogu zatražiti sjedišta.',
    'publish.submitted.review':
      'Poslato — vaša vožnja je na pregledu. Obavijestićemo vas kada bude objavljena (obično <24h).',
    'publish.seeMyRides': 'Pogledaj moje vožnje',
    'common.today': 'Danas',
    'common.tomorrow': 'Sutra',
    'onboarding.welcome.b4.title': 'Dvije uloge, jedna aplikacija',
    'onboarding.welcome.b4.body':
      'Putnici traže mjesta, vozači ih nude. Promijenite ulogu bilo kada u profilu.',
    'search.empty.changeSearch': 'Promijeni pretragu',
    'share.cta': 'Podijeli vožnju s porodicom',
    'share.text': 'Prati moju Waylo vožnju:',
    'shared.subtitle': '{name} putuje sa Waylo',
    'shared.departure': 'Polazak',
    'shared.driver': 'Vozač',
    'shared.status.scheduled': 'Zakazano',
    'shared.status.onway': 'Na putu',
    'shared.status.completed': 'Završeno',
    'shared.status.cancelled': 'Otkazano',
    'shared.footer': 'Privatno dijeljeno preko Waylo. Tačna lokacija se ne prikazuje.',
    'shared.unavailable.t': 'Vožnja nedostupna',
    'shared.unavailable.b': 'Link je možda istekao ili je isključen.',
    'pickup.step.t': 'Mjesto polaska (opcionalno)',
    'pickup.label': 'Mjesto preuzimanja',
    'pickup.labelPh': 'npr. ispred autobuske stanice',
    'pickup.tapHint': 'Dodirnite mapu da postavite oznaku.',
    'pickup.useLocation': 'Koristi moju lokaciju',
    'pickup.pinSet': 'Oznaka postavljena ✓',
    'pickup.optionalHint': 'Opcionalno — pomaže putnicima da vas nađu.',
    'pickup.cardTitle': 'Mjesto preuzimanja',
    'pickup.directions': 'Otvori u mapama →',
    'pickup.afterBooking': 'Tačno mjesto se prikazuje nakon odobrenja rezervacije.',
    'confirm.title': 'Potvrda vožnje',
    'confirm.done': 'Potvrđeno — vidimo se!',
    'confirm.pending': 'Potvrdite da i dalje dolazite.',
    'confirm.cta': 'I dalje dolazim',
    'confirm.driver.done': 'Polazak potvrđen',
    'confirm.driver.pending': 'Potvrdite da i dalje vozite.',
    'confirm.driver.cta': 'I dalje vozim',
    'confirm.driverConfirmed': 'Vozač je potvrdio polazak',
    'confirm.driverPending': 'Čeka se potvrda vozača',
    'comfort.ac': 'Klima',
    'comfort.charger': 'USB punjač',
    'comfort.middleSeat': 'Srednje sjedište',
    'booking.luggage': 'Prtljag',
    'booking.luggage.warn':
      'Ovaj vozač preferira prtljag do {max}. I dalje možete zatražiti — samo to spomenite u poruci.',
    'reliability.reliable': 'Pouzdan',
    'reliability.building': 'Gradi reputaciju',
    'reliability.label': 'Pouzdanost',
    'reliability.whatis.q': 'Šta je ovo?',
    'reliability.whatis.a':
      'Pouzdanost pokazuje koliko često ovaj vozač potvrđuje, kreće na vrijeme i završava vožnje. Ažurira se automatski na osnovu stvarnih vožnji — niko je ne podešava ručno.',
    'reliability.yours': 'Vaša pouzdanost: {n}',
    'search.empty.setAlert': 'Postavi obavještenje za liniju',
    'alert.set.title': 'Obavijesti me o ovoj liniji',
    'alert.set.body': 'Javit ćemo vam u Telegramu kada neko objavi vožnju na ovoj liniji.',
    'alert.set.cta': 'Postavi obavještenje',
    'alert.set.toast': 'Gotovo — javit ćemo vam u Telegramu.',
    'alert.more': 'Više opcija',
    'alert.maxPrice': 'Maks. cijena (opcionalno)',
    'alert.delete': 'Obriši obavještenje',
    'profile.alerts.title': 'Obavještenja za linije',
    'booking.seats': 'Mjesta',
    'booking.companions': 'Saputnici',
    'booking.companions.hint': 'Opcionalno — navedite ko putuje s vama da vozač zna.',
    'booking.companions.ph': 'Ime putnika {n}',
    'publish.step.route.t': 'Odaberi liniju',
    'publish.stepCount': 'Korak {n} od {total}',
    'publish.route.incomplete': 'Odaberite oba grada s liste da nastavite.',
    'publish.route.mode.preset': 'Predefinisana',
    'publish.route.mode.custom': 'Prilagođena',
    'publish.route.custom.hint':
      'Odaberite bilo koja dva grada. Predložit ćemo fer cijenu i dodati liniju da je drugi mogu naći.',
    'publish.step.route.via.t': 'Usputne stanice (opcionalno)',
    'publish.step.route.via.hint':
      'Odaberite gradove kroz koje prolazite. Putnici mogu ući ili izaći u bilo kom od njih.',
    'publish.step.route.via.placeholder': 'Ukucajte ime grada…',
    'publish.step.route.via.empty': 'Nema podudaranja.',
    'ride.via.short': 'preko {stops}',
    'ride.via.long': 'preko {stops}',
    'publish.step.when.t': 'Kada vozite?',
    'publish.step.when.date': 'Datum',
    'publish.step.when.time': 'Vrijeme polaska',
    'publish.step.when.flex': 'Fleksibilnost',
    'publish.step.when.flexExact': 'Tačno — bez fleksa',
    'publish.step.when.flex10': '± 10 minuta',
    'publish.step.when.flex30': '± 30 minuta',
    'publish.step.when.flex60': '± 1 sat',
    'publish.step.seats.t': 'Koliko sjedišta?',
    'publish.step.approval.t': 'Način odobravanja',
    'publish.step.approval.hint':
      'Ručno odobravanje vam dozvoljava da pregledate svaki zahtjev prije prihvatanja.',
    'publish.step.approval.manual': 'Ručno',
    'publish.step.approval.auto': 'Auto-odobravanje',
    'publish.step.cost.t': 'Cijena po sjedištu',
    'publish.step.cost.label': 'Po sjedištu ({currency})',
    'publish.step.cost.refTitle': 'Referentna cijena',
    'publish.step.cost.refBody':
      'Samo preporuka: {ref} {currency}. Vi odlučujete — naplatite manje ako želite samo društvo, ili pratite referencu da podijelite gorivo + putarine.',
    'publish.step.cost.warn':
      'Iznad preporučene cijene. Putnici mogu da preskoče — odluka je vaša.',
    'publish.step.cost.reject':
      'Znatno iznad preporuke (preko 1.5× referente). Vjerovatno će odbiti putnike — odluka je vaša.',
    'publish.step.prefs.t': 'Preference',
    'publish.step.prefs.music': 'Muzika dozvoljena',
    'publish.step.prefs.smoking': 'Pušenje dozvoljeno',
    'publish.step.prefs.pets': 'Kućni ljubimci dozvoljeni',
    'publish.step.prefs.women': 'Vožnja samo za žene',
    'publish.step.prefs.luggage': 'Maksimalan prtljag po putniku',
    'publish.step.prefs.luggage.small': 'Mali (ručni prtljag)',
    'publish.step.prefs.luggage.medium': 'Srednji (veliki ranac)',
    'publish.step.prefs.luggage.large': 'Veliki (kofer)',
    'publish.step.notes.t': 'Detalji pokupljanja (opciono)',
    'publish.step.notes.label': 'Napomene za pokupljanje',
    'publish.step.notes.ph':
      'npr. Pokupljanje na centralnoj autobuskoj stanici, sjeverni izlaz.',
    'publish.step.review.t': 'Pregled vožnje',
    'publish.step.review.route': 'Linija',
    'publish.step.review.departure': 'Polazak',
    'publish.step.review.seats': 'Sjedišta',
    'publish.step.review.cost': 'Cijena',
    'publish.step.review.approval': 'Odobravanje',
    'publish.cta.next': 'Dalje',
    'publish.cta.publishing': 'Objavljivanje…',
    'publish.cta.publish': 'Objavi vožnju',
    'publish.error.overpriced':
      'Cijena {cost} {currency} prekoračuje 1.5× referentne. Molimo prilagodite.',

    'my.driver.title': 'Vozač',
    'my.driver.tabRides': 'Moje vožnje',
    'my.driver.tabRequests': 'Pristigli zahtjevi',
    'my.driver.publish': 'Objavi',
    'my.driver.noRides.t': 'Još nema vožnji',
    'my.driver.noRides.b':
      'Objavite prvu vožnju da pronađete putnike koji idu istim putem.',
    'my.driver.noRequests.t': 'Nema zahtjeva',
    'my.driver.noRequests.b': 'Zahtjevi za rezervaciju od putnika će se pojaviti ovdje.',

    'my.bookings.title': 'Moje rezervacije',
    'my.bookings.empty.t': 'Još nema rezervacija',
    'my.bookings.empty.b':
      'Pronađite vožnju i zatražite sjedište — rezervacije se prikazuju ovdje.',
    'my.bookings.id': 'Rezervacija #{id}',

    'booking.title': 'Rezervacija',
    'booking.passenger': 'Putnik',
    'booking.details': 'Detalji rezervacije',
    'booking.openRide': 'Otvori detalje vožnje',
    'booking.chatWith': 'Otvori chat sa {name} na Telegramu',
    'booking.rate.title': 'Ocijeni putovanje',
    'booking.rate.commentPh': 'Opcioni komentar (biće javno vidljiv)',
    'booking.rate.submit': 'Pošalji ocjenu',
    'booking.rate.sending': 'Slanje…',
    'booking.decision.decline': 'Odbij',
    'booking.decision.approve': 'Odobri',
    'booking.complete': 'Označi kao završeno',
    'booking.cancel': 'Otkaži rezervaciju',
    'booking.reportLink': 'Prijavi problem sa rezervacijom',
    'booking.report.reason': 'Razlog',
    'booking.report.note': 'Šta se desilo?',
    'booking.report.submit': 'Pošalji prijavu',
    'booking.report.reason.no_show': 'Nedolazak',
    'booking.report.reason.dangerous_driving': 'Opasna vožnja',
    'booking.report.reason.overcharging': 'Preplaćivanje',
    'booking.report.reason.commercial_activity': 'Komercijalna djelatnost',
    'booking.report.reason.harassment': 'Uznemiravanje',
    'booking.report.reason.spam': 'Spam',
    'booking.report.reason.other': 'Drugo',
    'booking.cantLoad': 'Nije moguće učitati rezervaciju.',

    'profile.section.profile': 'Profil',
    'profile.section.car': 'Podaci o automobilu',
    'profile.section.car.hint': 'Pomaže putnicima da vas pronađu prilikom pokupljanja.',
    'profile.section.account': 'Račun',
    'profile.logout': 'Odjava',
    'profile.displayName': 'Prikazano ime',
    'profile.bio': 'Opis',
    'profile.driverMode': 'Vozački režim',
    'profile.car.make': 'Marka',
    'profile.car.model': 'Model',
    'profile.car.color': 'Boja',
    'profile.car.plate': 'Registracija (opciono)',
    'profile.language': 'Jezik',
    'profile.save': 'Sačuvaj profil',
    'profile.ridesCompleted': '{n} vožnji',

    'public.about': 'O korisniku',

    'premium.title': 'Waylo je besplatan — zauvijek',
    'premium.subtitle': 'Osnivački članovi pomažu da finansiramo razvoj.',
    'premium.tag': 'Osnivački član',
    'premium.active': 'Aktivno',
    'premium.price.value': '50 ⭐',
    'premium.price.cadence': '/ mjesec · ≈ 2 KM',
    'premium.features.title': 'Šta podržavaoci dobijaju',
    'premium.features.l1':
      'Neograničeno objavljivanje vožnji (besplatni nivo već pokriva 15 / mjesec)',
    'premium.features.l2': 'Bedž osnivačkog člana na profilu',
    'premium.features.l3': 'Prioritetna podrška tima',
    'premium.features.l4': 'Direktno pomažete razvoj Waylo-a — projekat kompanije Triardor',
    'premium.disclaimer':
      'Waylo radi u potpunosti bez plaćanja. Podrška je opciona i plaća se u Telegram Stars — Telegramovoj internoj valuti — pa nikada ne vidimo vašu karticu.',
    'premium.cta.buy': 'Podrži Waylo · 50 ⭐',
    'premium.cta.renew': 'Obnovi podršku · 50 ⭐',
    'common.madeByTriardor': 'Razvija Triardor',

    'badge.founding_driver': 'osnivački vozač',
    'badge.community_verified': 'verifikovan u zajednici',
    'badge.trusted_route_regular': 'pouzdan na liniji',
    'badge.consistent': 'pouzdan',
    'badge.first_ride': 'prva vožnja',
    'badge.early_supporter': 'rani sljedbenik',
    'tier.newcomer': 'Pridošlica',
    'tier.regular': 'Redovan vozač',
    'tier.reliable': 'Pouzdan vozač',
    'tier.trusted': 'Poverljiv vozač',
    'tier.ambassador': 'Ambasador',
    'profile.memberSince': 'Član od {date}',
    'badge.premium': 'premium',

    'status.full': 'Popunjeno',
    'status.under_review': 'Na pregledu',
    'status.cancelled': 'Otkazano',
    'status.completed': 'Završeno',
    'status.pending': 'na čekanju',
    'status.approved': 'odobreno',
    'status.declined': 'odbijeno',
    'bs.pending': 'Na čekanju',
    'bs.approved': 'Odobreno',
    'bs.declined': 'Odbijeno',
    'bs.cancelled_by_passenger': 'Vi ste otkazali',
    'bs.cancelled_by_driver': 'Vozač je otkazao',
    'bs.completed': 'Završeno',
    'bs.rated': 'Ocijenjeno',
    'bs.auto_closed': 'Zatvoreno',
    'bs.no_show': 'Nedolazak',
    'bs.under_review': 'Na pregledu',

    'luggage.small': 'Mali',
    'luggage.medium': 'Srednji',
    'luggage.large': 'Veliki',

    'legal.privacy': 'Politika privatnosti',
    'legal.terms': 'Uslovi korištenja',
    'calendar.tab': 'Kalendar',
    'calendar.empty': 'Nema vožnji ovog dana.',
    'my.driver.tabList': 'Lista',
    'vibes.title': 'Stil putovanja',
    'vibes.hint':
      'Vaše zadane vrijednosti. Predpopunjavamo ih u svaku vožnju koju objavite i prikazujemo na javnom profilu, da putnici znaju šta da očekuju.',
    'vibes.notSet': '—',
    'vibes.yes': 'Da',
    'vibes.no': 'Ne',
    'vibes.music': 'Muzika u autu',
    'vibes.smoking': 'Pušenje dozvoljeno',
    'vibes.pets': 'Ljubimci dozvoljeni',
    'vibes.womenOnly': 'Vožnja samo za žene',
    'vibes.chatLevel': 'Koliko sam pričljiv/a?',
    'vibes.chat.quiet': 'Tih',
    'vibes.chat.sometimes': 'Ponekad',
    'vibes.chat.chatty': 'Pričljiv',
    'vibes.luggage': 'Maks. prtljag',
    'phone.label': 'Broj telefona',
    'phone.placeholder': '+387 60 123 4567',
    'phone.hint':
      'Međunarodni format sa pozivnim brojem. Prikazuje se samo potvrđenim saputnicima (i samo ako uključite dijeljenje ispod).',
    'phone.invalid': 'Koristite međunarodni format, npr. +387601234567',
    'privacy.title': 'Privatnost',
    'privacy.shareTelegram': 'Dijeli moj Telegram @username nakon odobrene rezervacije',
    'privacy.sharePhone': 'Dijeli moj broj telefona nakon odobrene rezervacije',
    'privacy.showBio': 'Prikaži moj opis na javnom profilu',
    'privacy.contactReveal':
      'Telefon i Telegram username se prikazuju TEK nakon odobrene rezervacije — nikada u rezultatima pretrage.',

    'contact.title': 'Kontakt podaci',
    'contact.beforeApproval':
      'Kontakt podaci će se pojaviti ovdje nakon što rezervacija bude odobrena.',
    'contact.tgUsername': 'Telegram',
    'contact.phone': 'Telefon',
    'contact.callButton': 'Pozovi',
    'contact.tgButton': 'Otvori chat na Telegramu',
    'contact.notShared': 'Nije podijeljen',

    'block.button': 'Blokiraj korisnika',
    'block.confirm': 'Blokirati {name}? Neće moći rezervisati vaše vožnje, niti ćete se viđati u aplikaciji.',
    'block.title': 'Blokirani korisnici',
    'block.empty': 'Niste nikoga blokirali.',
    'block.unblock': 'Odblokiraj',

    'onboarding.phone.title': 'Dodajte broj telefona (opciono)',
    'onboarding.phone.hint':
      'Možete dodati kasnije. Dijeli se samo sa saputnicima nakon odobrene rezervacije — nikada u pretrazi.',
    'onboarding.phone.skip': 'Preskoči za sada',

    'err.UNAUTHORIZED': 'Molimo prijavite se ponovo.',
    'err.FORBIDDEN': 'Nemate dozvolu za ovu radnju.',
    'err.NOT_FOUND': 'Nije pronađeno.',
    'err.VALIDATION_FAILED': 'Neka polja su nepopunjena ili nevažeća.',
    'err.INVALID_INIT_DATA': 'Telegram autentifikacija nije uspjela. Probajte ponovo otvoriti aplikaciju.',
    'err.ANTI_ABUSE_REJECT':
      'Već ste danas više puta objavili ovu rutu. Waylo je za dijeljenje putovanja, ne za komercijalne linije — pokušajte ponovo sutra.',
    'err.FREE_TIER_LIMIT':
      'Besplatni vozači mogu objaviti do 4 vožnje mjesečno. Pređite na Premium za neograničeno objavljivanje.',
    'err.RIDE_NOT_FOUND': 'Vožnja više ne postoji.',
    'err.RIDE_NOT_BOOKABLE': 'Vožnja više ne prima rezervacije.',
    'err.SUSPENDED':
      'Račun je privremeno pauziran zbog ponovljenih otkazivanja u posljednji čas. Pauza se automatski ukida uskoro.',
    'err.RIDE_NOT_STARTED': 'Vozač još nije započeo vožnju.',
    'err.INSUFFICIENT_SEATS': 'Nema dovoljno slobodnih sjedišta.',
    'err.DUPLICATE_BOOKING': 'Već imate rezervaciju na ovoj vožnji.',
    'err.CANNOT_BOOK_OWN_RIDE': 'Ne možete rezervisati svoju vožnju.',
    'err.ILLEGAL_TRANSITION': 'Ova radnja nije moguća u trenutnom statusu rezervacije.',
    'err.RATE_LIMITED': 'Previše zahtjeva — sačekajte trenutak i pokušajte ponovo.',
    'err.ROUTE_NOT_FOUND': 'Linija nije dostupna.',
    'err.NOT_A_DRIVER': 'Uključite vozački režim u profilu prije objavljivanja vožnji.',
    'err.INVALID_DEPARTURE': 'Datum polaska je nevažeći.',
    'err.DEPARTURE_TOO_SOON': 'Polazak mora biti najmanje 5 minuta u budućnosti.',
    'err.CANNOT_BLOCK_SELF': 'Ne možete blokirati sebe.',
    'err.ALREADY_RATED': 'Već ste ocijenili ovu vožnju.',
    'err.BOOKING_NOT_COMPLETED': 'Možete ocijeniti vožnju nakon završetka.',
    'err.BOOKING_NOT_FOUND': 'Rezervacija više ne postoji.',
    'err.USER_NOT_FOUND': 'Korisnik nije pronađen.',
    'err.PAYMENTS_DISABLED': 'Plaćanja nisu konfigurisana u ovom okruženju.',
    'err.UNKNOWN': 'Nešto je pošlo po zlu. Pokušajte ponovo.',
    'err.INTERNAL': 'Greška na serveru. Pokušajte za trenutak.',
    'err.SAFETY_WINDOW_CLOSED': 'Sigurnosno upozorenje moguće je samo u roku od 24h od vožnje.',
    'err.NOT_PARTICIPANT': 'Niste dio ove rezervacije.',
    'error.cantLoad.t': 'Nije moguće učitati',
    'error.cantLoad.b': 'Problem s mrežom ili serverom. Pokušajte ponovo.',
    'error.cantLoad.retry': 'Pokušaj ponovo',
    'error.cantLoad.retrying': 'Pokušavam…',

    'safety.title': 'Sigurnost',
    'safety.cardTitle': 'Osjećate nesigurnost? Pozovite pomoć',
    'safety.cardBody':
      'Pritisnite dugme ispod ako vam je tokom vožnje neprijatno. Možete pozvati hitne službe i/ili obavijestiti Waylo + svoj kontakt za hitne slučajeve jednim potezom.',
    'safety.openSheet': '🚨 Treba mi pomoć',
    'safety.sheet.title': 'Pozovite pomoć',
    'safety.sheet.callEmergency': 'Pozovite hitne službe (112)',
    'safety.sheet.callEmergencyHint':
      'Evropski broj za hitne slučajeve — povezuje sa policijom, hitnom pomoći ili vatrogascima.',
    'safety.sheet.notifyWaylo': 'Obavijesti Waylo + moj hitni kontakt',
    'safety.sheet.notifyWayloHint':
      'Waylo tim za sigurnost se obavještava sa detaljima vožnje. Vaš hitni kontakt će vam biti prikazan da ga sami pozovete.',
    'safety.sheet.note': 'Šta se dešava? (opciono)',
    'safety.sheet.notePh': 'npr. Vozač me čini neprijatnim.',
    'safety.sheet.notifySent': 'Waylo i vaš hitni kontakt su obaviješteni.',
    'safety.sheet.notifyFailed': 'Slanje nije uspjelo. Pozovite hitne službe ako je hitno.',
    'safety.sheet.callContactBtn': 'Pozovi {name}',
    'safety.cancel': 'Otkaži',

    'profile.emergency.title': 'Hitni kontakt',
    'profile.emergency.hint':
      'Kontaktira se samo ako pritisnete dugme za sigurnost tokom vožnje. Nikada se ne dijeli sa drugim korisnicima.',
    'profile.emergency.name': 'Ime kontakta',
    'profile.emergency.phone': 'Telefon kontakta',

    'weekday.mon': 'Pon',
    'weekday.tue': 'Uto',
    'weekday.wed': 'Sri',
    'weekday.thu': 'Čet',
    'weekday.fri': 'Pet',
    'weekday.sat': 'Sub',
    'weekday.sun': 'Ned',

    'publish.step.repeat.t': 'Ponavljanje svake sedmice?',
    'publish.step.repeat.hint':
      'Uključite ako redovno vozite ovu rutu. Objavit ćemo vožnju za svaki odabrani dan, do 4 sedmice unaprijed.',
    'publish.step.repeat.off': 'Jednokratna vožnja',
    'publish.step.repeat.on': 'Ponavljajuća',
    'publish.step.repeat.allWeekdays': 'Pon–Pet',
    'publish.step.repeat.weekend': 'Sub i Ned',
    'publish.step.repeat.pickDays': 'Odaberite dane',
    'publish.step.repeat.atLeastOne': 'Odaberite bar jedan dan',
    'publish.submitted.recurring':
      'Vaša ponavljajuća vožnja je aktivna. Buduće vožnje će se objavljivati automatski — upravljajte iz Moji šabloni.',
    'home.s.templates.t': 'Moji šabloni',
    'home.s.templates.b': 'Ponavljajuće vožnje',
    'my.templates.title': 'Ponavljajuće vožnje',
    'my.templates.empty.t': 'Još nema ponavljajućih vožnji',
    'my.templates.empty.b':
      'Uključite „Ponavljajuća" prilikom objavljivanja vožnje za postavljanje sedmičnog rasporeda.',
    'my.templates.upcoming': '{n} predstojećih vožnji',
    'my.templates.everyWeek': 'svakog {days} u {time}',
    'my.templates.cancel': 'Zaustavi ovaj šablon',
    'my.templates.cancelConfirm':
      'Zaustaviti ponavljajuću vožnju i otkazati sve predstojeće vožnje?',
    'my.templates.cancelled': 'Šablon zaustavljen. {n} predstojećih vožnji otkazano.',

    'err.TEMPLATE_NOT_FOUND': 'Šablon nije pronađen.',

    'refer.title': 'Pozovi prijatelje',
    'refer.hint':
      'Waylo raste sa svojom zajednicom. Podijelite svoj kod — što više ljudi na vašim linijama, to više vožnji za sve.',
    'refer.code': 'Vaš kod',
    'refer.stats': '{count} pozvano · {rewarded} sada vozi',
    'refer.share': 'Pošalji poziv',
    'refer.copied': 'Kod je kopiran',
    'refer.showQr': 'Prikaži QR kod',
    'refer.hideQr': 'Sakrij QR kod',
    'refer.qrCaption': 'Skenirajte kamerom telefona da se pridružite Waylo-u',
    'refer.shareText':
      'Probaj Waylo — besplatan carpool unutar Telegrama. Bez provizije, samo realno dijeljenje troškova putovanja.\n\nKoristi moj kod {code}: {url}',
    'products.title': 'Pogledajte naše druge proizvode',
    'products.cta': 'Pogledaj →',

    'tripStatus.title': 'Ažuriranja vožnje',
    'tripStatus.driverTitle': 'Pošalji brzo ažuriranje',
    'tripStatus.driverHint':
      'Obavijestite putnike o stanju. Svaki dodir šalje Telegram poruku svima sa odobrenom rezervacijom.',
    'tripStatus.empty': 'Još nema objavljenih ažuriranja.',
    'tripStatus.leaving_soon': 'Uskoro krećem',
    'tripStatus.at_pickup': 'Stigao sam na mjesto pokupljanja',
    'tripStatus.departed': 'Krenuli smo',
    'tripStatus.arrived': 'Stigli smo na odredište',
    'tripStatus.sent': 'Ažuriranje poslato',
    'tripStatus.windowClosed': 'Ažuriranja možete slati od 2h prije do 24h poslije polaska.',
    'err.STATUS_WINDOW_CLOSED': 'Ažuriranja možete slati od 2h prije do 24h poslije polaska.',
    'editRide.title': 'Izmijeni vožnju',
    'editRide.lockedHint':
      'Neko je već rezervisao, pa su vrijeme, cijena i ruta zaključani. Otkaži i objavi ponovo da bi ih promijenio.',
    'editRide.cantEdit': 'Ova vožnja se više ne može izmijeniti.',
    'editRide.save': 'Sačuvaj izmjene',
    'editRide.noChanges': 'Još nema izmjena',
    'segment.title': 'Gdje ulaziš i izlaziš?',
    'segment.hint': 'Ova vožnja prolazi kroz usputne stanice — izaberi svoj dio puta.',
    'segment.board': 'Ulazak u',
    'segment.alight': 'Izlazak u',
    'segment.invalid': 'Izlazak mora biti poslije ulaska.',
    'segment.price': 'Tvoja cijena',
    'segment.allow': 'Dozvoli djelimične vožnje',
    'segment.allow.hint':
      'Putnici mogu rezervisati samo dio rute (npr. do usputne stanice). Cijena se dijeli po udaljenosti.',
    'segment.yourTrip': 'Tvoj dio: {from} → {to}',
  },

  hr: {
    'app.tagline': 'Podijelite put.',
    'app.connecting': 'Povezivanje…',
    'app.errorPrefix': 'Nije moguće povezati se s Waylom:',
    'app.retry': 'Pokušaj ponovo',
    'app.openInTelegram.title': 'Waylo se koristi unutar Telegrama',
    'app.openInTelegram.body':
      'Otvorite @WayloRide_bot u Telegramu i pritisnite plavi gumb izbornika pored polja za poruku.',
    'app.openInTelegram.cta': 'Otvori u Telegramu',
    'common.back': 'Natrag',
    'common.continue': 'Nastavi',
    'common.save': 'Spremi',
    'common.saving': 'Spremanje…',
    'common.saved': 'Spremljeno',
    'common.cancel': 'Odustani',
    'common.submit': 'Pošalji',
    'common.optional': 'neobavezno',
    'common.loading': 'Učitavanje…',
    'common.working': 'Radim…',
    'common.refTo': 'Referentna cijena',
    'common.perSeat': 'po sjedalu',
    'common.seat_one': 'sjedalo',
    'common.seat_other': 'sjedala',
    'common.rideStarred': '★',
    'nav.search': 'Pretraga',
    'nav.bookings': 'Rezervacije',
    'nav.rides': 'Vožnje',
    'nav.profile': 'Profil',

    'onboarding.welcome.title': 'Dobrodošli u Waylo',
    'onboarding.welcome.tagline': 'Podijelite put. Podijelite troškove.',
    'onboarding.welcome.b1.title': 'Pronađite ljude koji idu istim putem',
    'onboarding.welcome.b1.body': 'Pretraga vožnji na 5 međugradskih linija.',
    'onboarding.welcome.b2.title': 'Podjela troškova, ne komercijala',
    'onboarding.welcome.b2.body': 'Cijene su oko referentne cijene radi pravednosti.',
    'onboarding.welcome.b3.title': 'Sve unutar Telegrama',
    'onboarding.welcome.b3.body':
      'Nema instalacije aplikacije. Prijava preko Telegrama.',
    'onboarding.language.title': 'Odaberite jezik',
    'onboarding.language.hint': 'Možete promijeniti u profilu bilo kada.',
    'onboarding.profile.title': 'Postavite profil',
    'onboarding.profile.hint': 'Popunili smo polja koja smo dobili s Telegrama.',
    'onboarding.profile.displayName': 'Prikazano ime',
    'onboarding.profile.bio': 'Kratak opis (neobavezno)',
    'onboarding.profile.bioPh':
      'npr. Putujem na relaciji Zagreb–Split većinu vikenda.',
    'onboarding.profile.driver': 'Želim ponekad voziti',
    'onboarding.profile.driverHint': 'Možete uključiti/isključiti bilo kada.',
    'onboarding.rules.title': 'Pravila zajednice',
    'onboarding.rules.hint': 'Kratko štivo. Strogo se primjenjuje.',
    'onboarding.rules.r1.t': 'Podjela troškova, ne taksi.',
    'onboarding.rules.r1.b':
      'Waylo služi za podjelu stvarnih troškova privatnog putovanja — ne za komercijalni prijevoz.',
    'onboarding.rules.r2.t': 'Cijena mora biti poštena.',
    'onboarding.rules.r2.b':
      'Referentna cijena je preporuka zajednice. Vi odlučujete. Iznad 1.5× prikazujemo upozorenje, ali se vožnja i dalje objavljuje — putnici glasuju rezervacijama.',
    'onboarding.rules.r3.t': 'Budite ljubazni.',
    'onboarding.rules.r3.b':
      'Uznemiravanje, opasna vožnja, neodazivanje — vode do suspenzije.',
    'onboarding.rules.r4.t': 'Nema osobnih kontakata u profilu.',
    'onboarding.rules.r4.b': 'Koristite Telegram chat unutar aplikacije.',
    'onboarding.rules.r5.t': 'Prijavite probleme.',
    'onboarding.rules.r5.b':
      'Koristite ugrađenu prijavu — moderatori čitaju svaku prijavu.',
    'onboarding.rules.accept':
      'Pročitao/la sam pravila i {termsLink}, i pristajem ih se pridržavati.',
    'onboarding.rules.terms': 'uvjete',
    'onboarding.cta.continue': 'Nastavi',
    'onboarding.cta.getStarted': 'Krenimo',
    'onboarding.cta.acceptToContinue': 'Prihvatite pravila za nastavak',

    'home.welcomeBack': 'Dobrodošli natrag',
    'home.title': 'Kamo putujete?',
    'home.subtitle': 'Nađite mjesto na nečijem putovanju ili ponudite svoja.',
    'home.cta.publish.t': 'Objavi vožnju',
    'home.cta.publish.b': 'Ponudite mjesta na putovanju koje planirate',
    'home.cta.becomeDriver.t': 'Nudite prijevoz?',
    'home.cta.becomeDriver.b': 'Uključite način vozača u profilu',
    'home.from': 'Od',
    'home.to': 'Do',
    'home.date': 'Datum',
    'home.search': 'Pretraži vožnje',
    'home.profile': 'Profil',
    'home.shortcuts': 'Prečaci',
    'home.s.publish.t': 'Objavi vožnju',
    'home.s.publish.b': 'Pokri troškove puta',
    'home.s.bookings.t': 'Moje rezervacije',
    'home.s.bookings.b': 'Putovanja koja ste tražili',
    'home.s.rides.t': 'Moje vožnje',
    'home.s.rides.b': 'Putovanja koja vozite',
    'home.s.premium.t': 'Waylo Premium',
    'home.s.premium.b': 'Neograničene vožnje',

    'search.title': 'Dostupne vožnje',
    'search.empty.title': 'Još nema vožnji za ovaj datum',
    'search.empty.body': 'Budite prvi koji objavljuje — ili provjerite kasnije.',
    'search.empty.cta': 'Objavi vožnju',
    'search.error': 'Nije moguće učitati vožnje.',

    'ride.cost': 'Cijena',
    'ride.cost.hint':
      'Referentna cijena za ovu liniju: {ref} {currency}. Prikazujemo je kako bi putnici mogli provjeriti je li cijena poštena.',
    'ride.cost.fair': 'poštena cijena',
    'ride.cost.warn': 'iznad referentne',
    'ride.cost.high': 'visoka',
    'ride.preferences': 'Preferencije',
    'ride.pickup': 'Preuzimanje',
    'ride.cancel': 'Odustani od vožnje',
    'ride.cancelConfirm': 'Otkazati ovu vožnju? Putnici će biti obaviješteni.',
    'ride.cantLoad': 'Nije moguće učitati vožnju.',
    'common.offline': 'Niste povezani — neke radnje možda neće raditi.',
    'profile.tier.title': 'Razina vozača',
    'profile.tier.toGo': 'Još {n} vožnji do: {next}',
    'profile.tier.max': 'Najviša razina 🏆',
    'city.more': 'Nastavite tipkati da suzite…',
    'my.driver.pastRides': 'Prošle vožnje',
    'my.driver.resolvedRequests': 'Riješeni zahtjevi',
    'my.archive.title': 'Arhiva',
    'my.archive.tabRides': 'Prošle vožnje',
    'my.archive.tabRequests': 'Riješeni zahtjevi',
    'my.archive.emptyRides': 'Još nema prošlih vožnji',
    'my.archive.emptyRequests': 'Još nema riješenih zahtjeva',
    'leaderboard.title': 'Ljestvica',
    'leaderboard.subtitle': 'Najbolji vozači po završenim vožnjama',
    'leaderboard.empty.title': 'Još nema poretka',
    'leaderboard.empty.body': 'Ljestvica se popunjava kako vozači završavaju vožnje — budite među prvima!',
    'leaderboard.rides': '{n} vožnji',
    'home.s.leaderboard.t': 'Ljestvica',
    'home.s.leaderboard.b': 'Najbolji vozači',
    'ride.requestSeat': 'Zatraži sjedalo',
    'ride.start': 'Započni vožnju',
    'ride.finish': 'Završi vožnju',
    'ride.finishConfirm': 'Završiti vožnju? Preostali putnici bit će označeni kao završeni.',
    'booking.arrived': 'Stigao/la sam',
    'booking.noShow': 'Označi da se putnik nije pojavio',
    'booking.noShowConfirm': 'Označiti {name} kao da se nije pojavio/la? Ovo snižava pouzdanost i ne može se poništiti.',
    'ride.messageToDriver': 'Poruka vozaču (neobavezno)',
    'ride.messageToDriverPh': 'Bok! Želim rezervirati sjedalo za…',
    'ride.pref.smoking.yes': 'Pušenje dozvoljeno',
    'ride.pref.smoking.no': 'Bez pušenja',
    'ride.pref.music.yes': 'Glazba dozvoljena',
    'ride.pref.music.no': 'Tiha vožnja',
    'ride.pref.pets.yes': 'Kućni ljubimci dozvoljeni',
    'ride.pref.pets.no': 'Bez kućnih ljubimaca',
    'ride.pref.luggage': 'Prtljaga',
    'ride.pref.womenOnly': 'Samo za žene',
    'ride.verified': 'Verificiran',
    'ride.driverRides': '{n} vožnji',

    'publish.driverModeFirst.t': 'Prvo uključite vozački način rada',
    'publish.driverModeFirst.b':
      'Vozači objavljuju vožnje na Waylo. Možete uključiti u profilu i isključiti bilo kada.',
    'publish.openProfile': 'Otvori profil',
    'publish.submitted.title': 'Vožnja je poslana',
    'publish.submitted.live':
      'Vaša vožnja je objavljena. Putnici sada mogu zatražiti sjedala.',
    'publish.submitted.review':
      'Poslano — vaša vožnja je na pregledu. Obavijestit ćemo vas kada bude objavljena (obično <24h).',
    'publish.seeMyRides': 'Pogledaj moje vožnje',
    'common.today': 'Danas',
    'common.tomorrow': 'Sutra',
    'onboarding.welcome.b4.title': 'Dvije uloge, jedna aplikacija',
    'onboarding.welcome.b4.body':
      'Putnici traže mjesta, vozači ih nude. Promijenite ulogu bilo kada u profilu.',
    'search.empty.changeSearch': 'Promijeni pretragu',
    'share.cta': 'Podijeli vožnju s obitelji',
    'share.text': 'Prati moju Waylo vožnju:',
    'shared.subtitle': '{name} putuje s Waylom',
    'shared.departure': 'Polazak',
    'shared.driver': 'Vozač',
    'shared.status.scheduled': 'Zakazano',
    'shared.status.onway': 'Na putu',
    'shared.status.completed': 'Završeno',
    'shared.status.cancelled': 'Otkazano',
    'shared.footer': 'Privatno dijeljeno preko Waylo. Točna lokacija se ne prikazuje.',
    'shared.unavailable.t': 'Vožnja nedostupna',
    'shared.unavailable.b': 'Link je možda istekao ili je isključen.',
    'pickup.step.t': 'Mjesto polaska (neobavezno)',
    'pickup.label': 'Mjesto preuzimanja',
    'pickup.labelPh': 'npr. ispred autobusnog kolodvora',
    'pickup.tapHint': 'Dodirnite kartu da postavite oznaku.',
    'pickup.useLocation': 'Koristi moju lokaciju',
    'pickup.pinSet': 'Oznaka postavljena ✓',
    'pickup.optionalHint': 'Neobavezno — pomaže putnicima da vas nađu.',
    'pickup.cardTitle': 'Mjesto preuzimanja',
    'pickup.directions': 'Otvori u kartama →',
    'pickup.afterBooking': 'Točno mjesto prikazuje se nakon odobrenja rezervacije.',
    'confirm.title': 'Potvrda vožnje',
    'confirm.done': 'Potvrđeno — vidimo se!',
    'confirm.pending': 'Potvrdite da i dalje dolazite.',
    'confirm.cta': 'I dalje dolazim',
    'confirm.driver.done': 'Polazak potvrđen',
    'confirm.driver.pending': 'Potvrdite da i dalje vozite.',
    'confirm.driver.cta': 'I dalje vozim',
    'confirm.driverConfirmed': 'Vozač je potvrdio polazak',
    'confirm.driverPending': 'Čeka se potvrda vozača',
    'comfort.ac': 'Klima',
    'comfort.charger': 'USB punjač',
    'comfort.middleSeat': 'Srednje sjedalo',
    'booking.luggage': 'Prtljaga',
    'booking.luggage.warn':
      'Ovaj vozač preferira prtljagu do {max}. I dalje možete zatražiti — samo to spomenite u poruci.',
    'reliability.reliable': 'Pouzdan',
    'reliability.building': 'Gradi reputaciju',
    'reliability.label': 'Pouzdanost',
    'reliability.whatis.q': 'Što je ovo?',
    'reliability.whatis.a':
      'Pouzdanost pokazuje koliko često ovaj vozač potvrđuje, kreće na vrijeme i završava vožnje. Ažurira se automatski na temelju stvarnih vožnji — nitko je ne postavlja ručno.',
    'reliability.yours': 'Vaša pouzdanost: {n}',
    'search.empty.setAlert': 'Postavi obavijest za liniju',
    'alert.set.title': 'Obavijesti me o ovoj liniji',
    'alert.set.body': 'Javit ćemo vam u Telegramu kada netko objavi vožnju na ovoj liniji.',
    'alert.set.cta': 'Postavi obavijest',
    'alert.set.toast': 'Gotovo — javit ćemo vam u Telegramu.',
    'alert.more': 'Više opcija',
    'alert.maxPrice': 'Maks. cijena (neobavezno)',
    'alert.delete': 'Obriši obavijest',
    'profile.alerts.title': 'Obavijesti za linije',
    'booking.seats': 'Mjesta',
    'booking.companions': 'Suputnici',
    'booking.companions.hint': 'Neobavezno — navedite tko putuje s vama da vozač zna.',
    'booking.companions.ph': 'Ime putnika {n}',
    'publish.step.route.t': 'Odaberite liniju',
    'publish.stepCount': 'Korak {n} od {total}',
    'publish.route.incomplete': 'Odaberite oba grada s popisa za nastavak.',
    'publish.route.mode.preset': 'Predefinirana',
    'publish.route.mode.custom': 'Prilagođena',
    'publish.route.custom.hint':
      'Odaberite bilo koja dva grada. Predložit ćemo fer cijenu i dodati liniju da je drugi mogu naći.',
    'publish.step.route.via.t': 'Usputne stanice (neobavezno)',
    'publish.step.route.via.hint':
      'Odaberite gradove kroz koje prolazite. Putnici mogu ući ili izaći u bilo kojem od njih.',
    'publish.step.route.via.placeholder': 'Upišite ime grada…',
    'publish.step.route.via.empty': 'Nema podudaranja.',
    'ride.via.short': 'preko {stops}',
    'ride.via.long': 'preko {stops}',
    'publish.step.when.t': 'Kada vozite?',
    'publish.step.when.date': 'Datum',
    'publish.step.when.time': 'Vrijeme polaska',
    'publish.step.when.flex': 'Fleksibilnost',
    'publish.step.when.flexExact': 'Točno — bez fleksa',
    'publish.step.when.flex10': '± 10 minuta',
    'publish.step.when.flex30': '± 30 minuta',
    'publish.step.when.flex60': '± 1 sat',
    'publish.step.seats.t': 'Koliko sjedala?',
    'publish.step.approval.t': 'Način odobravanja',
    'publish.step.approval.hint':
      'Ručno odobravanje vam omogućuje pregled svakog zahtjeva prije prihvaćanja.',
    'publish.step.approval.manual': 'Ručno',
    'publish.step.approval.auto': 'Automatsko',
    'publish.step.cost.t': 'Cijena po sjedalu',
    'publish.step.cost.label': 'Po sjedalu ({currency})',
    'publish.step.cost.refTitle': 'Referentna cijena',
    'publish.step.cost.refBody':
      'Samo preporuka: {ref} {currency}. Vi odlučujete — naplatite manje ako želite samo društvo, ili pratite referencu da podijelite gorivo + cestarine.',
    'publish.step.cost.warn':
      'Iznad preporučene cijene. Putnici je mogu preskočiti — odluka je vaša.',
    'publish.step.cost.reject':
      'Znatno iznad preporuke (preko 1.5× referentne). Vjerojatno će odbiti putnike — odluka je vaša.',
    'publish.step.prefs.t': 'Preferencije',
    'publish.step.prefs.music': 'Glazba dozvoljena',
    'publish.step.prefs.smoking': 'Pušenje dozvoljeno',
    'publish.step.prefs.pets': 'Kućni ljubimci dozvoljeni',
    'publish.step.prefs.women': 'Vožnja samo za žene',
    'publish.step.prefs.luggage': 'Maksimalna prtljaga po putniku',
    'publish.step.prefs.luggage.small': 'Mala (ručna prtljaga)',
    'publish.step.prefs.luggage.medium': 'Srednja (veliki ruksak)',
    'publish.step.prefs.luggage.large': 'Velika (kofer)',
    'publish.step.notes.t': 'Detalji preuzimanja (neobavezno)',
    'publish.step.notes.label': 'Napomene za preuzimanje',
    'publish.step.notes.ph':
      'npr. Preuzimanje na središnjem autobusnom kolodvoru, sjeverni izlaz.',
    'publish.step.review.t': 'Pregled vožnje',
    'publish.step.review.route': 'Linija',
    'publish.step.review.departure': 'Polazak',
    'publish.step.review.seats': 'Sjedala',
    'publish.step.review.cost': 'Cijena',
    'publish.step.review.approval': 'Odobravanje',
    'publish.cta.next': 'Dalje',
    'publish.cta.publishing': 'Objavljivanje…',
    'publish.cta.publish': 'Objavi vožnju',
    'publish.error.overpriced':
      'Cijena {cost} {currency} prelazi 1.5× referentne. Molimo prilagodite.',

    'my.driver.title': 'Vozač',
    'my.driver.tabRides': 'Moje vožnje',
    'my.driver.tabRequests': 'Pristigli zahtjevi',
    'my.driver.publish': 'Objavi',
    'my.driver.noRides.t': 'Još nema vožnji',
    'my.driver.noRides.b':
      'Objavite prvu vožnju kako biste pronašli putnike koji idu istim putem.',
    'my.driver.noRequests.t': 'Nema zahtjeva',
    'my.driver.noRequests.b': 'Zahtjevi za rezervaciju od putnika će se pojaviti ovdje.',

    'my.bookings.title': 'Moje rezervacije',
    'my.bookings.empty.t': 'Još nema rezervacija',
    'my.bookings.empty.b':
      'Pronađite vožnju i zatražite sjedalo — rezervacije se prikazuju ovdje.',
    'my.bookings.id': 'Rezervacija #{id}',

    'booking.title': 'Rezervacija',
    'booking.passenger': 'Putnik',
    'booking.details': 'Detalji rezervacije',
    'booking.openRide': 'Otvori detalje vožnje',
    'booking.chatWith': 'Otvori chat s {name} na Telegramu',
    'booking.rate.title': 'Ocijeni putovanje',
    'booking.rate.commentPh': 'Neobavezni komentar (bit će javno vidljiv)',
    'booking.rate.submit': 'Pošalji ocjenu',
    'booking.rate.sending': 'Slanje…',
    'booking.decision.decline': 'Odbij',
    'booking.decision.approve': 'Odobri',
    'booking.complete': 'Označi kao završeno',
    'booking.cancel': 'Odustani od rezervacije',
    'booking.reportLink': 'Prijavi problem s rezervacijom',
    'booking.report.reason': 'Razlog',
    'booking.report.note': 'Što se dogodilo?',
    'booking.report.submit': 'Pošalji prijavu',
    'booking.report.reason.no_show': 'Neodazivanje',
    'booking.report.reason.dangerous_driving': 'Opasna vožnja',
    'booking.report.reason.overcharging': 'Preplaćivanje',
    'booking.report.reason.commercial_activity': 'Komercijalna djelatnost',
    'booking.report.reason.harassment': 'Uznemiravanje',
    'booking.report.reason.spam': 'Spam',
    'booking.report.reason.other': 'Drugo',
    'booking.cantLoad': 'Nije moguće učitati rezervaciju.',

    'profile.section.profile': 'Profil',
    'profile.section.car': 'Podaci o automobilu',
    'profile.section.car.hint':
      'Pomaže putnicima da vas pronađu prilikom preuzimanja.',
    'profile.section.account': 'Račun',
    'profile.logout': 'Odjava',
    'profile.displayName': 'Prikazano ime',
    'profile.bio': 'Opis',
    'profile.driverMode': 'Vozački način rada',
    'profile.car.make': 'Marka',
    'profile.car.model': 'Model',
    'profile.car.color': 'Boja',
    'profile.car.plate': 'Registracija (neobavezno)',
    'profile.language': 'Jezik',
    'profile.save': 'Spremi profil',
    'profile.ridesCompleted': '{n} vožnji',

    'public.about': 'O korisniku',

    'premium.title': 'Waylo je besplatan — zauvijek',
    'premium.subtitle': 'Osnivački članovi pomažu da financiramo razvoj.',
    'premium.tag': 'Osnivački član',
    'premium.active': 'Aktivno',
    'premium.price.value': '50 ⭐',
    'premium.price.cadence': '/ mjesec · ≈ €1',
    'premium.features.title': 'Što podržavatelji dobivaju',
    'premium.features.l1':
      'Neograničeno objavljivanje vožnji (besplatna razina već pokriva 15 / mjesec)',
    'premium.features.l2': 'Bedž osnivačkog člana na profilu',
    'premium.features.l3': 'Prioritetna podrška tima',
    'premium.features.l4': 'Izravno pomažete razvoj Waylo-a — projekt tvrtke Triardor',
    'premium.disclaimer':
      'Waylo radi u potpunosti bez plaćanja. Podrška je opcionalna i plaća se u Telegram Stars — Telegramovoj internoj valuti — pa nikada ne vidimo vašu karticu.',
    'premium.cta.buy': 'Podrži Waylo · 50 ⭐',
    'premium.cta.renew': 'Obnovi podršku · 50 ⭐',
    'common.madeByTriardor': 'Razvija Triardor',

    'badge.founding_driver': 'osnivački vozač',
    'badge.community_verified': 'verificiran u zajednici',
    'badge.trusted_route_regular': 'pouzdan na liniji',
    'badge.premium': 'premium',
    'badge.consistent': 'pouzdan',
    'badge.first_ride': 'prva vožnja',
    'badge.early_supporter': 'rani sljedbenik',
    'tier.newcomer': 'Pridošlica',
    'tier.regular': 'Redovan vozač',
    'tier.reliable': 'Pouzdan vozač',
    'tier.trusted': 'Provjeren vozač',
    'tier.ambassador': 'Ambasador',
    'profile.memberSince': 'Član od {date}',

    'status.full': 'Popunjeno',
    'status.under_review': 'Na pregledu',
    'status.cancelled': 'Otkazano',
    'status.completed': 'Završeno',
    'status.pending': 'na čekanju',
    'status.approved': 'odobreno',
    'status.declined': 'odbijeno',
    'bs.pending': 'Na čekanju',
    'bs.approved': 'Odobreno',
    'bs.declined': 'Odbijeno',
    'bs.cancelled_by_passenger': 'Vi ste otkazali',
    'bs.cancelled_by_driver': 'Vozač je otkazao',
    'bs.completed': 'Završeno',
    'bs.rated': 'Ocijenjeno',
    'bs.auto_closed': 'Zatvoreno',
    'bs.no_show': 'Nedolazak',
    'bs.under_review': 'Na pregledu',

    'luggage.small': 'Malen',
    'luggage.medium': 'Srednji',
    'luggage.large': 'Velik',

    'legal.privacy': 'Pravila privatnosti',
    'legal.terms': 'Uvjeti korištenja',
    'calendar.tab': 'Kalendar',
    'calendar.empty': 'Nema vožnji ovog dana.',
    'my.driver.tabList': 'Popis',
    'vibes.title': 'Stil putovanja',
    'vibes.hint':
      'Vaše zadane vrijednosti. Pred-popunjavamo ih u svaku vožnju koju objavite i prikazujemo na javnom profilu, da putnici znaju što očekivati.',
    'vibes.notSet': '—',
    'vibes.yes': 'Da',
    'vibes.no': 'Ne',
    'vibes.music': 'Glazba u autu',
    'vibes.smoking': 'Pušenje dozvoljeno',
    'vibes.pets': 'Ljubimci dozvoljeni',
    'vibes.womenOnly': 'Vožnja samo za žene',
    'vibes.chatLevel': 'Koliko sam pričljiv/a?',
    'vibes.chat.quiet': 'Tih',
    'vibes.chat.sometimes': 'Ponekad',
    'vibes.chat.chatty': 'Pričljiv',
    'vibes.luggage': 'Maks. prtljaga',
    'phone.label': 'Broj telefona',
    'phone.placeholder': '+385 91 123 4567',
    'phone.hint':
      'Međunarodni format s pozivnim brojem. Prikazuje se samo potvrđenim suputnicima (i samo ako uključite dijeljenje ispod).',
    'phone.invalid': 'Koristite međunarodni format, npr. +385911234567',
    'privacy.title': 'Privatnost',
    'privacy.shareTelegram': 'Podijeli moj Telegram @username nakon odobrene rezervacije',
    'privacy.sharePhone': 'Podijeli moj broj telefona nakon odobrene rezervacije',
    'privacy.showBio': 'Prikaži moj opis na javnom profilu',
    'privacy.contactReveal':
      'Telefon i Telegram username prikazuju se TEK nakon odobrene rezervacije — nikada u rezultatima pretrage.',

    'contact.title': 'Kontakt podaci',
    'contact.beforeApproval':
      'Kontakt podaci pojavit će se ovdje nakon što rezervacija bude odobrena.',
    'contact.tgUsername': 'Telegram',
    'contact.phone': 'Telefon',
    'contact.callButton': 'Nazovi',
    'contact.tgButton': 'Otvori chat na Telegramu',
    'contact.notShared': 'Nije podijeljen',

    'block.button': 'Blokiraj korisnika',
    'block.confirm': 'Blokirati {name}? Neće moći rezervirati vaše vožnje, niti ćete se viđati u aplikaciji.',
    'block.title': 'Blokirani korisnici',
    'block.empty': 'Niste nikoga blokirali.',
    'block.unblock': 'Odblokiraj',

    'onboarding.phone.title': 'Dodajte broj telefona (neobavezno)',
    'onboarding.phone.hint':
      'Možete dodati kasnije. Dijeli se samo sa suputnicima nakon odobrene rezervacije — nikada u pretrazi.',
    'onboarding.phone.skip': 'Preskoči za sada',

    'err.UNAUTHORIZED': 'Molimo prijavite se ponovno.',
    'err.FORBIDDEN': 'Nemate dopuštenje za tu radnju.',
    'err.NOT_FOUND': 'Nije pronađeno.',
    'err.VALIDATION_FAILED': 'Neka polja su nepopunjena ili nevažeća.',
    'err.INVALID_INIT_DATA': 'Telegram autentifikacija nije uspjela. Pokušajte ponovno otvoriti aplikaciju.',
    'err.ANTI_ABUSE_REJECT':
      'Već ste danas više puta objavili ovu rutu. Waylo je za dijeljenje putovanja, ne za komercijalne linije — pokušajte ponovo sutra.',
    'err.FREE_TIER_LIMIT':
      'Besplatni vozači mogu objaviti do 4 vožnje mjesečno. Prijeđite na Premium za neograničeno objavljivanje.',
    'err.RIDE_NOT_FOUND': 'Vožnja više ne postoji.',
    'err.RIDE_NOT_BOOKABLE': 'Vožnja više ne prima rezervacije.',
    'err.SUSPENDED':
      'Račun je privremeno pauziran zbog ponovljenih otkazivanja u posljednji čas. Pauza se automatski ukida uskoro.',
    'err.RIDE_NOT_STARTED': 'Vozač još nije započeo vožnju.',
    'err.INSUFFICIENT_SEATS': 'Nema dovoljno slobodnih sjedala.',
    'err.DUPLICATE_BOOKING': 'Već imate rezervaciju na ovoj vožnji.',
    'err.CANNOT_BOOK_OWN_RIDE': 'Ne možete rezervirati vlastitu vožnju.',
    'err.ILLEGAL_TRANSITION': 'Ova radnja nije moguća u trenutnom statusu rezervacije.',
    'err.RATE_LIMITED': 'Previše zahtjeva — pričekajte trenutak i pokušajte ponovno.',
    'err.ROUTE_NOT_FOUND': 'Linija nije dostupna.',
    'err.NOT_A_DRIVER': 'Uključite vozački način u profilu prije objavljivanja vožnji.',
    'err.INVALID_DEPARTURE': 'Datum polaska je nevažeći.',
    'err.DEPARTURE_TOO_SOON': 'Polazak mora biti najmanje 5 minuta u budućnosti.',
    'err.CANNOT_BLOCK_SELF': 'Ne možete blokirati sebe.',
    'err.ALREADY_RATED': 'Već ste ocijenili ovo putovanje.',
    'err.BOOKING_NOT_COMPLETED': 'Možete ocijeniti vožnju nakon završetka.',
    'err.BOOKING_NOT_FOUND': 'Rezervacija više ne postoji.',
    'err.USER_NOT_FOUND': 'Korisnik nije pronađen.',
    'err.PAYMENTS_DISABLED': 'Plaćanja nisu konfigurirana u ovom okruženju.',
    'err.UNKNOWN': 'Nešto je pošlo po krivu. Pokušajte ponovno.',
    'err.INTERNAL': 'Greška na poslužitelju. Pokušajte za trenutak.',
    'err.SAFETY_WINDOW_CLOSED': 'Sigurnosno upozorenje moguće je samo unutar 24h od vožnje.',
    'err.NOT_PARTICIPANT': 'Niste dio ove rezervacije.',
    'error.cantLoad.t': 'Nije moguće učitati',
    'error.cantLoad.b': 'Problem s mrežom ili poslužiteljem. Pokušajte ponovno.',
    'error.cantLoad.retry': 'Pokušaj ponovno',
    'error.cantLoad.retrying': 'Pokušavam…',

    'safety.title': 'Sigurnost',
    'safety.cardTitle': 'Osjećate se nesigurno? Pozovite pomoć',
    'safety.cardBody':
      'Pritisnite gumb ispod ako vam je tijekom vožnje neugodno. Možete pozvati hitne službe i/ili obavijestiti Waylo + svoj kontakt za hitne slučajeve jednim potezom.',
    'safety.openSheet': '🚨 Trebam pomoć',
    'safety.sheet.title': 'Pozovite pomoć',
    'safety.sheet.callEmergency': 'Nazovite hitne službe (112)',
    'safety.sheet.callEmergencyHint':
      'Europski broj za hitne slučajeve — povezuje s policijom, hitnom pomoći ili vatrogascima.',
    'safety.sheet.notifyWaylo': 'Obavijesti Waylo + moj hitni kontakt',
    'safety.sheet.notifyWayloHint':
      'Waylov tim za sigurnost obavještava se s detaljima vožnje. Vaš hitni kontakt će vam biti prikazan da ga sami nazovete.',
    'safety.sheet.note': 'Što se događa? (neobavezno)',
    'safety.sheet.notePh': 'npr. Vozač mi je neugodan.',
    'safety.sheet.notifySent': 'Waylo i vaš hitni kontakt su obaviješteni.',
    'safety.sheet.notifyFailed': 'Slanje nije uspjelo. Nazovite hitne službe ako je hitno.',
    'safety.sheet.callContactBtn': 'Nazovi {name}',
    'safety.cancel': 'Odustani',

    'profile.emergency.title': 'Hitni kontakt',
    'profile.emergency.hint':
      'Kontaktira se samo ako pritisnete gumb za sigurnost tijekom vožnje. Nikad se ne dijeli s drugim korisnicima.',
    'profile.emergency.name': 'Ime kontakta',
    'profile.emergency.phone': 'Telefon kontakta',

    'weekday.mon': 'Pon',
    'weekday.tue': 'Uto',
    'weekday.wed': 'Sri',
    'weekday.thu': 'Čet',
    'weekday.fri': 'Pet',
    'weekday.sat': 'Sub',
    'weekday.sun': 'Ned',

    'publish.step.repeat.t': 'Ponavljanje svaki tjedan?',
    'publish.step.repeat.hint':
      'Uključite ako redovito vozite ovu rutu. Objavit ćemo vožnju za svaki odabrani dan, do 4 tjedna unaprijed.',
    'publish.step.repeat.off': 'Jednokratna vožnja',
    'publish.step.repeat.on': 'Ponavljajuća',
    'publish.step.repeat.allWeekdays': 'Pon–Pet',
    'publish.step.repeat.weekend': 'Sub i Ned',
    'publish.step.repeat.pickDays': 'Odaberite dane',
    'publish.step.repeat.atLeastOne': 'Odaberite bar jedan dan',
    'publish.submitted.recurring':
      'Vaša ponavljajuća vožnja je aktivna. Buduće vožnje će se objavljivati automatski — upravljajte iz Moji predlošci.',
    'home.s.templates.t': 'Moji predlošci',
    'home.s.templates.b': 'Ponavljajuće vožnje',
    'my.templates.title': 'Ponavljajuće vožnje',
    'my.templates.empty.t': 'Još nema ponavljajućih vožnji',
    'my.templates.empty.b':
      'Uključite „Ponavljajuća" prilikom objavljivanja vožnje za postavljanje tjednog rasporeda.',
    'my.templates.upcoming': '{n} nadolazećih vožnji',
    'my.templates.everyWeek': 'svakog {days} u {time}',
    'my.templates.cancel': 'Zaustavi ovaj predložak',
    'my.templates.cancelConfirm':
      'Zaustaviti ponavljajuću vožnju i otkazati sve nadolazeće vožnje?',
    'my.templates.cancelled': 'Predložak zaustavljen. {n} nadolazećih vožnji otkazano.',

    'err.TEMPLATE_NOT_FOUND': 'Predložak nije pronađen.',

    'refer.title': 'Pozovi prijatelje',
    'refer.hint':
      'Waylo raste sa svojom zajednicom. Podijelite svoj kod — što više ljudi na vašim linijama, to više vožnji za sve.',
    'refer.code': 'Vaš kod',
    'refer.stats': '{count} pozvano · {rewarded} sada vozi',
    'refer.share': 'Pošalji poziv',
    'refer.copied': 'Kod kopiran',
    'refer.showQr': 'Prikaži QR kod',
    'refer.hideQr': 'Sakrij QR kod',
    'refer.qrCaption': 'Skenirajte kamerom telefona za pridruživanje Waylo-u',
    'refer.shareText':
      'Isprobaj Waylo — besplatan carpool unutar Telegrama. Bez provizije, samo stvarno dijeljenje putnih troškova.\n\nKoristi moj kod {code}: {url}',
    'products.title': 'Pogledajte naše druge proizvode',
    'products.cta': 'Pogledaj →',

    'tripStatus.title': 'Ažuriranja vožnje',
    'tripStatus.driverTitle': 'Pošalji brzo ažuriranje',
    'tripStatus.driverHint':
      'Obavijestite putnike o stanju. Svaki dodir šalje Telegram poruku svima s odobrenom rezervacijom.',
    'tripStatus.empty': 'Još nema objavljenih ažuriranja.',
    'tripStatus.leaving_soon': 'Uskoro krećem',
    'tripStatus.at_pickup': 'Stigao sam na mjesto preuzimanja',
    'tripStatus.departed': 'Krenuli smo',
    'tripStatus.arrived': 'Stigli smo na odredište',
    'tripStatus.sent': 'Ažuriranje poslano',
    'tripStatus.windowClosed': 'Ažuriranja možete slati od 2h prije do 24h poslije polaska.',
    'err.STATUS_WINDOW_CLOSED': 'Ažuriranja možete slati od 2h prije do 24h poslije polaska.',
    'editRide.title': 'Uredi vožnju',
    'editRide.lockedHint':
      'Netko je već rezervirao, pa su vrijeme, cijena i ruta zaključani. Otkaži i objavi ponovo da bi ih promijenio.',
    'editRide.cantEdit': 'Ova se vožnja više ne može urediti.',
    'editRide.save': 'Spremi izmjene',
    'editRide.noChanges': 'Još nema izmjena',
    'segment.title': 'Gdje ulaziš i izlaziš?',
    'segment.hint': 'Ova vožnja prolazi kroz usputne stanice — odaberi svoj dio puta.',
    'segment.board': 'Ulazak u',
    'segment.alight': 'Izlazak u',
    'segment.invalid': 'Izlazak mora biti nakon ulaska.',
    'segment.price': 'Tvoja cijena',
    'segment.allow': 'Dopusti djelomične vožnje',
    'segment.allow.hint':
      'Putnici mogu rezervirati samo dio rute (npr. do usputne stanice). Cijena se dijeli po udaljenosti.',
    'segment.yourTrip': 'Tvoj dio: {from} → {to}',
  },
} as const satisfies Record<SupportedLocale, Record<string, string>>;

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  sr: 'Srpski',
  bs: 'Bosanski',
  hr: 'Hrvatski',
};
